import React, {
  useState,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import { Tooltip } from "../Tooltip";
import styles from "./pastQueries.module.css";
import InforIcon from "../../assets/InfoCircleIcon";
import SimpleBackIcon from "../../assets/simpleBackIcon.svg";
import ExpandIcon from "../../assets/ExpandIcon";
import TrailIcon from "../../assets/TrailIcon";
import StopIcon from "../../assets/Stop.svg";
import DoneIcon from "../../assets/done.svg";
import CancelledIcon from "../../assets/cancelled.svg";
import RunningIcon from "../../assets/running.gif";
import DeletedIcon from "../../assets/deleted.svg";
import DV360Icon from "../../assets/dv360.svg";
import PubMaticIcon from "../../assets/pubMatic.png";
import GoogleAdsIcon from "../../assets/googleads.png";
import MetaIcon from "../../assets/meta.svg";
import AWSIcon from "../../assets/aws.svg";
import TTDIcon from "../../assets/ttd.svg";
import AzureIcon from "../../assets/azure.png";
import MediasmartIcon from "../../assets/mediasmart.png";
import GCPIcon from "../../assets/gcp.png";
import SFTPIcon from "../../assets/sftp.jpg";
import DownIcon from "../../assets/DownIcon";
import FilterIcon from "../../assets/FilterIcon";
import { HistoryIcon } from "../../assets/HistoryIcon";
import { PauseIcon } from "../../assets/PauseIcon";
import { PlayIcon } from "../../assets/PlayIcon";
import SearchModal from "../SearchModal";
import DatePickerModal from "../DatePicker";
import Dropdown from "../DropdownModal";
import { AuthContext } from "../../contexts/auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";
import {
  capitalize,
  getCSSVariable,
  formatDateTime,
  formatDateTimeParts,
  getLastMonthRange,
  toDateValue,
} from "../../utils";
import { useHistory, useLocation } from "react-router-dom";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { useToast } from "../../contexts/toast-context";
import InfoPopup from "../InfoPopup";
import PublishedCohortDetails from "./PublishedCohortDetails";
import { indexPublishedByQuery, PublishedDetails } from "./publishedDetails";
import CopyIcon from "../../assets/CopyIcon";
import NoData from "../NoData";
import ActivationPopup from "../ActivationChannels/ActivationPopup";
import PageNotFound from "../PageNotFound";
import PercentageLoader from "../PercentageLoader";
import DownloadButton from "../DownloadButton";
import LayoutContainer from "../LayoutContainer";
// import { ReloadIcon } from "../../assets/ReloadIcon";
import ColHeader from "../ColHeader";
import {
  MdDescription,
  MdGroup,
  MdFingerprint,
  MdNumbers,
  MdCheckCircle,
  MdCalendarToday,
  MdPlayArrow,
  MdBarChart,
  MdSpeed,
  MdInfoOutline,
  MdCampaign,
  MdLayers,
  MdTrackChanges,
} from "react-icons/md";
import CampaignVolumesModal from "../CampaignVolumesModal";
import QueryBreakdownModal, {
  QueryBreakdownData,
} from "../QueryBreakdownModal";
import AudienceOverviewModal from "../AudienceOverviewModal";
import {
  useLimitsUsage,
  ObjectiveUsageResult,
  StatusTone,
  tableStatus,
  ActivationUsage,
  WARNING_THRESHOLD_OFFSET,
} from "../../hooks/use-limits-usage";

interface TableRow {
  id: string;
  name: string;
  identifier: string;
}

// Visual distinction for a query row's objective: activation and enrichment
// are the two objectives with real backend limits/side effects, so they get
// their own color + icon; everything else (compound queries, etc.) reads as
// the neutral default "Audience" chip. "audienceTargeting" is just an older
// name for "activation" (see getActivationUsageForRow's same normalization
// further down) - not a distinct objective, so it gets the Activation badge
// too.
const OBJECTIVE_BADGES = {
  activation: { label: "Activation", Icon: MdCampaign, tone: "activation" },
  enrichment: { label: "Enrichment", Icon: MdLayers, tone: "enrichment" },
} as const;

// Mirrors LimitsUsageBanner's own STATUS_CLASS map so a table's status reads
// the same way (same colors/labels) in both surfaces.
const LIMITS_STATUS_CLASS: Record<StatusTone, string> = {
  ok: styles.limitsStatusOk,
  warning: styles.limitsStatusWarning,
  reached: styles.limitsStatusReached,
  disabled: styles.limitsStatusDisabled,
  inactive: styles.limitsStatusInactive,
};

// Solid fill colors for the engagement progress bar - LIMITS_STATUS_CLASS's
// colors are tuned for a light pill badge, too pale to read as a filled bar.
const ENGAGEMENT_PROGRESS_CLASS: Record<StatusTone, string> = {
  ok: styles.engagementProgressOk,
  warning: styles.engagementProgressWarning,
  reached: styles.engagementProgressReached,
  disabled: styles.engagementProgressOk,
  inactive: styles.engagementProgressOk,
};

// Same icon/label set ActivationChannels uses for these connector ids, so an
// "enabled connector" reads as the same brand everywhere it shows up.
const CONNECTOR_META: Record<string, { label: string; icon: string }> = {
  dv360: { label: "DV360", icon: DV360Icon },
  pubmatic: { label: "PubMatic", icon: PubMaticIcon },
  googleads: { label: "Google Ads", icon: GoogleAdsIcon },
  ttd: { label: "The Trade Desk", icon: TTDIcon },
  meta: { label: "Meta Ad Server", icon: MetaIcon },
  aws: { label: "AWS", icon: AWSIcon },
  azure: { label: "Azure", icon: AzureIcon },
  mediasmart: { label: "Mediasmart", icon: MediasmartIcon },
  gcp: { label: "GCP", icon: GCPIcon },
  sftp: { label: "SFTP", icon: SFTPIcon },
};

const getConnectorMeta = (connector: string) =>
  CONNECTOR_META[connector?.toLowerCase?.()] ?? {
    label: connector,
    icon: null,
  };

// Mirrors tableStatus' thresholds (same WARNING_THRESHOLD_OFFSET) so an
// engagement's status badge reads the same way a table's does, even though
// ActivationUsage has no enabled_flag/is_live to gate on - it's always a live,
// enabled counter.
function activationStatus(
  usage: ActivationUsage,
): { label: string; tone: StatusTone } {
  if (usage.activation_count >= usage.activation_limit) {
    return { label: "Limit reached", tone: "reached" };
  }
  if (usage.activation_count >= usage.activation_limit - WARNING_THRESHOLD_OFFSET) {
    return { label: "Nearing limit", tone: "warning" };
  }
  return { label: "OK", tone: "ok" };
}

const getObjectiveBadge = (objective: string) => {
  const normalized =
    objective === "audienceTargeting" ? "activation" : objective;
  return (
    OBJECTIVE_BADGES[normalized] ?? {
      label: "Audience",
      Icon: MdTrackChanges,
      tone: "audience",
    }
  );
};

const PastQueries = () => {
  const {
    getquery,
    putquery,
    updatequery,
    deletequery,
    getOrgByFederationID,
    getConfig,
    getrunningquery,
    getCampaignReportsCSV,
    getCampaignVolumes,
    triggerForQueryPercentage,
    putqueryAI,
    getCampaignNames,
    patchQueryCohort,
    patchQueryCpm,
    getFederationPublished,
    getCategories,
    putCategory,
    // user,
  } = useContext(AuthContext);
  const [progressMap, setProgressMap] = useState<{ [queryId: string]: number }>(
    {},
  );

  const { setBreadcrumbItems } = useBreadcrumb();
  const { showToast } = useToast();
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();
  const history = useHistory();
  const location = useLocation();
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const [activeCompoundRowId, setActiveCompoundRowId] = useState<string | null>(
    null,
  );
  const [highlightedSourceIds, setHighlightedSourceIds] = useState<Set<string>>(
    new Set(),
  );
  const [filterValue, setFilterValue] = useState({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [openSearchQueryIDModal, setOpenSearchQueryIDModal] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [openMatchIDDropdown, setOpenMatchIDDropdown] = useState(false);
  const [openParticipantsDropdown, setOpenParticipantsDropdown] =
    useState(false);
  const [filterMatchId, setMatchId] = useState<any>(undefined);
  const [filterCampaignName, setCampaignName] = useState<any>(undefined);
  const [filterQueryId, setFilterQueryId] = useState<any>(undefined);
  const filterQueryIdRef = useRef(filterQueryId);
  const filterCampaignNameRef = useRef(filterCampaignName);
  const filterMatchIdRef = useRef(filterMatchId);
  const [filterParticipant, setFilterParticipant] = useState<any>(undefined);
  const filterParticipantRef = useRef(filterParticipant);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageKey = useRef<{ key: string; scan_index_forward: boolean }>(
    undefined,
  );
  const currentPageRef = useRef<number>(currentPage);
  const pageDepth = useRef(0);
  const [filter_query, setfilter_query] = useState<any>(undefined);
  const [filterStartDate, setFilterStart] = useState<any>(undefined);
  const [filterEndDate, setFilterEnd] = useState<any>(undefined);
  const filterStartDateRef = useRef(filterStartDate);
  const FilterEndDateRef = useRef(filterEndDate);
  const [, setOrgName] = useState<any>(undefined);
  const [visiblePages, setVisiblePages] = useState([1, 2, 3]);
  const [rowIcons, setRowIcons] = useState({});
  const [rowStatusIcons] = useState({});
  const [filterApplied, setFilterApplied] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [openQueryIdPopup, setOpenQueryIdPopup] = useState<string | null>(null);
  const [openFailedInfoPopup, setOpenFailedInfoPopup] = useState<string | null>(
    null,
  );

  const [copiedQueryField, setCopiedQueryField] = useState<string | null>(null);
  const [showActivationPopup, setShowActivationPopup] = useState<
    false | Record<string, any>
  >(false);
  const [showDeletePopup, setShowDeletePopup] = useState<null | {
    row: any;
    index: number;
    actionType: "delete" | "cancel";
  }>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState("");
  const [showFailurePopup, setShowFailurePopup] = useState<string | boolean>(
    false,
  );
  const [, setQueryRowData] = useState({});
  const [refreshIn, setRefreshIn] = useState(15);
  const [failureMessage, setFailureMessage] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [autoRefreshPaused, setAutoRefreshPaused] = useState(() => {
    const saved = localStorage.getItem("autoRefreshPausedReportsPage");
    return saved ? JSON.parse(saved) : false;
  });
  const prevPageTableDataRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [isLastPage, setIsLastPage] = useState(false);
  const skipNextTableDataSync = useRef(false);
  const [columnWidths, setColumnWidths] = useState({
    1: "160px",
    2: "120px",
    3: "90px",
    4: "90px",
    5: "60px",
    6: "155px",
    7: "185px",
  });
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showLimitsUsagePopup, setShowLimitsUsagePopup] = useState(false);
  const { fetchUsage: fetchLimitsUsageData, loading: limitsUsageLoading } =
    useLimitsUsage();
  const [limitsUsageResults, setLimitsUsageResults] = useState<
    ObjectiveUsageResult[]
  >([]);
  const [allCampaignNames, setAllCampaignNames] = useState<
    { id: string; name: string }[]
  >([]);
  // Categories a query can be published under as a cohort.
  const [allCategories, setAllCategories] = useState<
    { id: string; name: string }[]
  >([]);
  const [showCampaignVolumesModal, setShowCampaignVolumesModal] =
    useState(false);
  const [breakdownModalData, setBreakdownModalData] =
    useState<QueryBreakdownData | null>(null);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [overviewRows, setOverviewRows] = useState<any[]>([]);
  const [overviewHasMore, setOverviewHasMore] = useState(false);
  const [overviewIsLoadingMore, setOverviewIsLoadingMore] = useState(false);

  const overviewIsLoadMoreRef = useRef(false);
  const overviewStartRef = useRef<string>("");
  const pageRootRef = useRef<HTMLDivElement>(null);
  const actionsBarRef = useRef<HTMLDivElement>(null);
  const tableControlsRef = useRef<HTMLDivElement>(null);
  const tableElRef = useRef<HTMLTableElement>(null);
  const tableFooterRef = useRef<HTMLDivElement>(null);
  const [tableWrapperMaxH, setTableWrapperMaxH] = useState<
    number | undefined
  >();
  const resizingRef = useRef({ index: null, startX: null, startWidth: null });
  const maxFirstColumnWidth = 600;
  const rowsPerPage = 8;
  var sessionFederationId = localStorage.getItem("federationkey");
  var sessionOrganizationId = localStorage.getItem("organizationkey");
  var allowedQueryObjectives: string[] = (() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("allowedQueryObjectives"));
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  })();
  // The selected federation has no real query objectives, so limits usage
  // falls back to the "activation" query_type (see fetchLimitsUsage below),
  // whose table/query-count data isn't meaningful - only the activation
  // section should render there. Real objectives still show activation_usage
  // alongside table/query data whenever the API returns it (a federation can
  // support both querying and activating on those results at once).
  const isActivationOnlyFederation = allowedQueryObjectives.length === 0;
  // registry-service treats a federation's enable_query_aggregations as opt-out,
  // not opt-in: unset/null (federations that predate this field) means enabled,
  // only an explicit false disables it. Mirror that here.
  const enableQueryAggregations =
    JSON.parse(localStorage.getItem("enableQueryAggregations") ?? "true") !==
    false;
  const storedPermissions = JSON.parse(localStorage.getItem("userPermissions"));
  const readAccess = storedPermissions?.query === "read";
  const writeAccess = storedPermissions?.query === "write";
  var organization_name = localStorage.getItem("organizationName");
  const getConfigFn = useAsyncFn({
    deferFn: getConfig,
    organizationId: sessionOrganizationId,
  });

  const userRole = localStorage.getItem("userRole");

  const fedSpecificMenuLabels = JSON.parse(
    localStorage.getItem("fedSpecificMenuLabels") || "{}",
  );

  useEffect(() => {
    localStorage.setItem(
      "autoRefreshPausedReportsPage",
      JSON.stringify(autoRefreshPaused),
    );
  }, [autoRefreshPaused]);

  useEffect(() => {
    if (sessionOrganizationId) {
      getConfigFn
        .run({ organizationId: sessionOrganizationId })
        .catch((err) => {
          console.error("Handled gracefully:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionOrganizationId]);

  useEffect(() => {
    filterMatchIdRef.current = filterMatchId;
  }, [filterMatchId]);

  useEffect(() => {
    filterQueryIdRef.current = filterQueryId;
  }, [filterQueryId]);

  useEffect(() => {
    filterParticipantRef.current = filterParticipant;
  }, [filterParticipant]);

  useEffect(() => {
    filterCampaignNameRef.current = filterCampaignName;
  }, [filterCampaignName]);

  useEffect(() => {
    filterStartDateRef.current = filterStartDate;
  }, [filterStartDate]);

  useEffect(() => {
    FilterEndDateRef.current = filterEndDate;
  }, [filterEndDate]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  var allIdentifiers =
    getConfigFn.data?.message?.canonical_identity_types ?? [];

  const identityTypeMap = new Map<string, string>(
    allIdentifiers.map(({ key, value }) => [key, value]),
  );


  var fedId = sessionFederationId;
  var orgId = sessionOrganizationId;

  const getQueriesFn = useAsyncFn({
    deferFn: getquery,
    federationId: fedId,
    organizationId: orgId,
    page_size: rowsPerPage,
    scan_index_forward: false,
    include_child_queries: true,
    nl_query: false,
    role: userRole,
  });
  const triggerForQueryPercentageFn = useAsyncFn({
    deferFn: triggerForQueryPercentage,
    federationId: fedId,
    organizationId: orgId,
    page_size: rowsPerPage,
    scan_index_forward: false,
    role: userRole,
  });
  const getRunningQueriesFn = useAsyncFn({
    promiseFn: getrunningquery,
    federationId: fedId,
    organizationId: orgId,
    page_size: rowsPerPage,
    scan_index_forward: false,
    role: userRole,
  });
  const getCampaignReportsCSVFn = useAsyncFn({
    deferFn: getCampaignReportsCSV,
    federationId: fedId,
    organizationId: orgId,
    scan_index_forward: false,
    role: userRole,
  });
  const getCampaignNamesFn = useAsyncFn({
    deferFn: getCampaignNames,
  });
  const getCampaignVolumesFn = useAsyncFn({
    deferFn: getCampaignVolumes,
    federationId: fedId,
    organizationId: orgId,
    page_size: rowsPerPage,
    scan_index_forward: false,
    include_child_queries: true,
    nl_query: false,
    role: userRole,
    exclude_zero_count: true,
    exclude_failed: true,
  });

  const getOverviewQueriesFn = useAsyncFn({
    deferFn: getquery,
    federationId: fedId,
    organizationId: orgId,
    page_size: 100,
    scan_index_forward: false,
    include_child_queries: true,
    nl_query: false,
    role: userRole,
  });
  const patchQueryCohortFn = useAsyncFn({
    deferFn: patchQueryCohort,
  });
  const patchQueryCpmFn = useAsyncFn({
    deferFn: patchQueryCpm,
  });
  const getCategoriesFn = useAsyncFn({
    deferFn: getCategories,
  });
  const putCategoryFn = useAsyncFn({
    deferFn: putCategory,
  });
  const [cohortPublishOverride, setCohortPublishOverride] = useState<
    Record<string, boolean>
  >({});
  const [cohortPublishLoading, setCohortPublishLoading] = useState<
    string | null
  >(null);
  const [publishCohortRow, setPublishCohortRow] = useState<any>(null);
  // CPM and category of published queries, which the queries list does not
  // carry. They come from the federation's published list, by query id.
  const [publishedDetails, setPublishedDetails] = useState<
    Record<string, PublishedDetails>
  >({});
  const [publishedLoading, setPublishedLoading] = useState<
    Record<string, boolean>
  >({});
  const [publishedLoadFailed, setPublishedLoadFailed] = useState<
    Record<string, boolean>
  >({});
  const publishedInFlight = useRef<Set<string>>(new Set());
  const [cohortNameInput, setCohortNameInput] = useState("");
  const [cohortDescriptionInput, setCohortDescriptionInput] = useState("");
  const [cohortNameError, setCohortNameError] = useState("");
  const [cohortDescriptionError, setCohortDescriptionError] = useState("");
  const [cohortDropdownOpen, setCohortDropdownOpen] = useState(false);
  const [cohortActiveIndex, setCohortActiveIndex] = useState(-1);
  // An exact name match reuses that category.
  const matchedCohortCategory = allCategories.find(
    (c) => c.name === cohortNameInput.trim(),
  );
  const filteredCohortCategories = cohortDropdownOpen
    ? allCategories.filter((c) =>
        c.name?.toLowerCase().includes(cohortNameInput.trim().toLowerCase()),
      )
    : [];
  const canPublishCohort =
    !!cohortNameInput.trim() &&
    !getCategoriesFn.isPending &&
    cohortPublishLoading !== publishCohortRow?.id;

  const closePublishCohortDialog = () => {
    setPublishCohortRow(null);
    setCohortNameInput("");
    setCohortDescriptionInput("");
    setCohortNameError("");
    setCohortDescriptionError("");
    setCohortDropdownOpen(false);
    setCohortActiveIndex(-1);
  };

  // Cancelling goes back to the Query Details popup the dialog was opened from.
  const cancelPublishCohortDialog = () => {
    if (!publishCohortRow || cohortPublishLoading === publishCohortRow.id) {
      return;
    }
    const rowId = publishCohortRow.id;
    closePublishCohortDialog();
    setOpenQueryIdPopup(rowId);
  };

  const selectCohortCategory = (category) => {
    setCohortNameInput(category.name);
    setCohortNameError("");
    setCohortDropdownOpen(false);
    setCohortActiveIndex(-1);
  };

  const handleCohortNameKeyDown = (e) => {
    const count = filteredCohortCategories.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!cohortDropdownOpen) {
        setCohortDropdownOpen(true);
        setCohortActiveIndex(0);
      } else if (count) {
        setCohortActiveIndex((i) => (i + 1) % count);
      }
    } else if (e.key === "ArrowUp" && count) {
      e.preventDefault();
      setCohortActiveIndex((i) => (i <= 0 ? count - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (count && cohortActiveIndex >= 0) {
        selectCohortCategory(filteredCohortCategories[cohortActiveIndex]);
      } else if (canPublishCohort) {
        publishCohort();
      }
    }
  };

  const handleCohortNameChange = (e) => {
    const val = e.target.value;
    setCohortDropdownOpen(val.length > 0);
    setCohortActiveIndex(-1);
    if (val.length > 40) {
      setCohortNameInput(val.slice(0, 40));
      setCohortNameError("Category name cannot exceed 40 characters.");
    } else {
      setCohortNameInput(val);
      setCohortNameError("");
    }
  };

  const handleCohortDescriptionChange = (e) => {
    const val = e.target.value;
    if (val.length > 100) {
      setCohortDescriptionInput(val.slice(0, 100));
      setCohortDescriptionError("Description cannot exceed 100 characters.");
    } else {
      setCohortDescriptionInput(val);
      setCohortDescriptionError("");
    }
  };

  // A category belongs to the query's federation, whichever organization is
  // publishing: an admin sees other organizations' queries too. The session's
  // federation is the fallback for a row that carries none.
  const cohortCategoryFederation = (row) =>
    row?.federation_id || sessionFederationId;

  // Categories are loaded each time the dialog opens, so ones created since
  // are offered. Every category of the query's federation is listed, each name
  // once, since categories made before they were federation-wide can repeat one.
  const refreshCohortCategories = (row) => {
    const fedId = cohortCategoryFederation(row);
    if (!fedId) return;
    setAllCategories([]);
    getCategoriesFn
      .run({ orgId, fedId })
      .then((result: any) => {
        const byName = new Map<string, { id: string; name: string }>();
        (result?.Items ?? result?.items ?? []).forEach((item: any) => {
          if (item.name && !byName.has(item.name)) {
            byName.set(item.name, { id: item.id, name: item.name });
          }
        });
        setAllCategories(Array.from(byName.values()));
      })
      .catch(() => {});
  };

  const loadPublishedDetails = async (fedId: string) => {
    if (!fedId || publishedInFlight.current.has(fedId)) return;
    publishedInFlight.current.add(fedId);
    setPublishedLoading((prev) => ({ ...prev, [fedId]: true }));
    setPublishedLoadFailed((prev) => ({ ...prev, [fedId]: false }));
    try {
      const data = await getFederationPublished({
        args: { federationId: fedId, organizationId: orgId, role: userRole },
      });
      setPublishedDetails((prev) => ({
        ...prev,
        ...indexPublishedByQuery(data),
      }));
    } catch (error) {
      console.error("Failed to load the published cohort details", error);
      setPublishedLoadFailed((prev) => ({ ...prev, [fedId]: true }));
    } finally {
      publishedInFlight.current.delete(fedId);
      setPublishedLoading((prev) => ({ ...prev, [fedId]: false }));
    }
  };

  // A query's details are dropped when it is published or unpublished, so the
  // popup never shows the category or CPM of an earlier publish.
  const forgetPublishedDetails = (queryId: string) =>
    setPublishedDetails((prev) => {
      if (!(queryId in prev)) return prev;
      const { [queryId]: _dropped, ...rest } = prev;
      return rest;
    });

  // Changes only the CPM of a published query. A refusal is thrown for the
  // popup to show beside the field, so the edit stays open to be corrected.
  const savePublishedCpm = async (row, cpm: number) => {
    setCohortPublishLoading(row.id);
    try {
      const data = await patchQueryCpmFn.run({
        queryId: row.id,
        cpm,
        organizationId: orgId,
      });
      setPublishedDetails((prev) => ({
        ...prev,
        [row.id]: {
          cpm: typeof data?.cpm === "number" ? data.cpm : cpm,
          categoryId: data?.category_id ?? prev[row.id]?.categoryId ?? "",
          categoryName: data?.category_name ?? prev[row.id]?.categoryName ?? "",
          categoryDescription:
            data?.category_description ?? prev[row.id]?.categoryDescription ?? "",
        },
      }));
      showToast("CPM updated successfully.", "success");
    } finally {
      setCohortPublishLoading(null);
    }
  };

  const unpublishCohort = async (row) => {
    setCohortPublishLoading(row.id);
    try {
      const data = await patchQueryCohortFn.run({
        queryId: row.id,
        body: { publish_cohort: false },
        organizationId: orgId,
      });
      setCohortPublishOverride((prev) => ({
        ...prev,
        [row.id]: data.publish_cohort,
      }));
      forgetPublishedDetails(row.id);
      setOpenQueryIdPopup(null);
      showToast("Cohort unpublished successfully.", "success");
    } catch (error: any) {
      showToast(
        error?.message || "Failed to update cohort publish state.",
        "error",
      );
    } finally {
      setCohortPublishLoading(null);
    }
  };

  // Publishing always calls the categories API first, then flags the query's
  // cohort as published under the category it returns. FQS answers a name that
  // a live category already has with that category, unchanged, so this reuses
  // an existing category and also copes with one that changed since the list
  // was loaded.
  const publishCohort = async () => {
    const row = publishCohortRow;
    const name = cohortNameInput.trim();
    if (!row || !name) return;
    setCohortPublishLoading(row.id);
    setCohortNameError("");
    let categoryId: string;
    try {
      const category = await putCategoryFn.run({
        body: {
          name,
          federation_id: cohortCategoryFederation(row),
          // The description only applies to a new category.
          description: matchedCohortCategory
            ? ""
            : cohortDescriptionInput.trim(),
        },
        organizationId: orgId,
      });
      categoryId = category?.id;
      if (!categoryId) {
        throw new Error("Failed to create category. Please try again.");
      }
      setAllCategories((prev) =>
        prev.some((c) => c.name === name)
          ? prev
          : [...prev, { id: categoryId, name }],
      );
    } catch (error: any) {
      const msg =
        error?.message || "Failed to create category. Please try again.";
      setCohortNameError(msg.charAt(0).toUpperCase() + msg.slice(1));
      setCohortPublishLoading(null);
      return;
    }
    try {
      const data = await patchQueryCohortFn.run({
        queryId: row.id,
        body: {
          publish_cohort: true,
          category_id: categoryId,
          category_name: name,
        },
        organizationId: orgId,
      });
      setCohortPublishOverride((prev) => ({
        ...prev,
        [row.id]: data.publish_cohort,
      }));
      forgetPublishedDetails(row.id);
      closePublishCohortDialog();
      setOpenQueryIdPopup(null);
      showToast("Cohort published successfully.", "success");
    } catch (error: any) {
      showToast(
        error?.message || "Failed to update cohort publish state.",
        "error",
      );
    } finally {
      setCohortPublishLoading(null);
    }
  };

  const [tableData, setTableData] = useState([]);

  // Opening the details of a published query reads its CPM and category.
  useEffect(() => {
    if (!openQueryIdPopup) return;
    const row: any = tableData?.find((r: any) => r.id === openQueryIdPopup);
    if (!row || row.is_deleted) return;
    if (!(cohortPublishOverride[row.id] ?? row.publish_cohort)) return;
    loadPublishedDetails(cohortCategoryFederation(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQueryIdPopup]);

  useLayoutEffect(() => {
    const compute = () => {
      if (
        !pageRootRef.current ||
        !actionsBarRef.current ||
        !tableControlsRef.current ||
        !tableFooterRef.current
      )
        return;
      // actionsBar: margin-top (--space-xxl=40) + margin-bottom (--space-sm=8) = 48
      // container: margin-top (--space-md=16) + margin-bottom (--space-md=16) = 32
      const maxH =
        pageRootRef.current.clientHeight -
        actionsBarRef.current.offsetHeight -
        48 -
        32 -
        tableControlsRef.current.offsetHeight -
        tableFooterRef.current.offsetHeight;
      setTableWrapperMaxH(Math.max(100, maxH));
    };
    compute();
    const ro = new ResizeObserver(() => requestAnimationFrame(compute));
    if (tableElRef.current) ro.observe(tableElRef.current);
    if (pageRootRef.current) ro.observe(pageRootRef.current);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [tableData]);

  var multipleIdData;

  multipleIdData = getQueriesFn.data && getQueriesFn.data.items;

  const handleMouseDown = (e, index) => {
    e.preventDefault();

    document.body.style.userSelect = "none";
    resizingRef.current = {
      index,
      startX: e.clientX,
      startWidth: parseInt(columnWidths[index], 10),
    };
    document.body.classList.add("no-select");
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const { index, startX, startWidth } = resizingRef.current;
    if (index !== null) {
      const deltaX = e.clientX - startX;
      let newWidth = Math.max(50, startWidth + deltaX);

      setColumnWidths((prev) => ({
        ...prev,
        [index]: `${newWidth}px`,
      }));
    }
  };

  const handleMouseUp = () => {
    resizingRef.current = { index: null, startX: null, startWidth: null };
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.classList.remove("no-select");
  };

  const handleCheckboxChange = (row: TableRow, checked: boolean) => {
    if (!row.identifier) {
      setErrorMessage("MatchID not found! Please select another query.");
      return;
    }

    if (checked) {
      const allMatch = selectedItems.every(
        (item) => item.identifier === row.identifier,
      );

      if (selectedItems.length === 0 || allMatch) {
        setSelectedItems((prev) => [...prev, row]);
      } else {
        setErrorMessage(
          "MatchID mismatch! Please choose queries with the same MatchID",
        );
      }
    } else {
      setSelectedItems((prev) => prev.filter((item) => item.id !== row.id));
    }
  };

  const updateProgress = (queryId: string, value: number) => {
    setProgressMap((prev) => ({
      ...prev,
      [queryId]: value,
    }));
  };

  const ShimmerRow = () => (
    <tr className={styles.shimmerRow}>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColIcon}`} />
      </td>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColName}`} />
      </td>
      <td>
        <div
          className={`${styles.shimmerCell} ${styles.shimmerColParticipant}`}
        />
      </td>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColMatchId}`} />
      </td>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColCount}`} />
      </td>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColStatus}`} />
      </td>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColDate}`} />
      </td>
      <td>
        <div className={`${styles.shimmerCell} ${styles.shimmerColAction}`} />
      </td>
    </tr>
  );

  const ButtonLoader = () => <div className={styles.smallSpinner}></div>;

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const navigateToProvTrailsPage = (queryId) => {
    if (queryId) history.push(`/provenance-record?queryid=${queryId}`);
  };
  const navigateToCampaignHistoryPage = (queryId) => {
    if (queryId) history.push(`/activation-history?queryid=${queryId}`);
  };

  const handleActive = async (row, rowIndex) => {
    if (row.status === "RUNNING") {
      setShowDeletePopup({ row, index: rowIndex, actionType: "cancel" });
      return;
    }
    setRowIcons((prev) => ({
      ...prev,
      [rowIndex]: getMarkButtonText({ running: true }),
    }));

    setShowActivationPopup(row);
    setQueryRowData(row);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      triggerForQueryPercentageFn.run().catch(() => {});
      getCampaignVolumesFn.run().catch(() => {});
      if (orgId) {
        getCampaignNamesFn
          .run({ orgId })
          .then((result: any) => {
            setAllCampaignNames(
              (result?.Items ?? []).map((item: any) => ({
                id: item.id,
                name: item.name,
              })),
            );
          })
          .catch(() => {});
      }

      const params = new URLSearchParams(location.search);
      const queryidFromUrl = params.get("queryid");
      if (queryidFromUrl) {
        setHighlightedRowId(queryidFromUrl);
        setFilterApplied(true);
        setFilterQueryId(queryidFromUrl);
        setFilterValue({ Query: queryidFromUrl });
      }

      try {
        await getQueriesFn.run(
          queryidFromUrl
            ? {
                promiseFn: getquery,
                federationId: fedId,
                organizationId: orgId,
                query_id: queryidFromUrl,
                page_size: rowsPerPage,
                role: userRole,
                include_child_queries: true,
                nl_query: false,
              }
            : undefined,
        );
      } catch (error) {
        if (error?.response?.status === 503) {
          setFailureMessage(
            "Service is temporarily unavailable. Please try again later.",
          );
        } else {
          setFailureMessage(
            "Something went wrong. Please refresh and try again.",
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Used only when GET /campaigns is empty or has no match for the typed name.
  const volumeCampaignNames = useMemo(() => {
    const seen = new Set<string>();
    const fromVolumes: { id: string; name: string }[] = [];
    for (const item of getCampaignVolumesFn.data?.items ?? []) {
      const name = item?.campaign_name;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      fromVolumes.push({ id: item.id || name, name });
    }
    return fromVolumes;
  }, [getCampaignVolumesFn.data]);

  const ClearFilters = async () => {
    setIsLastPage(false);
    setCurrentPage(1);
    currentPageKey.current = undefined;
    pageDepth.current = 0;
    setFilterValue({});
    setfilter_query(undefined);
    setFilterStart(undefined);
    setFilterEnd(undefined);
    FilterQueryId = "";
    setMatchId(undefined);
    setOrgName(undefined);
    setCampaignName(undefined);
    setFilterQueryId(undefined);
    setFilterParticipant(undefined);
    setSortColumn(null);
    setSortDirection("asc");
    setLoading(true);
    try {
      const response = await getQueriesFn.run({
        federationId: fedId,
        organizationId: orgId,
        page_size: rowsPerPage,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
        nl_query: false,
      });

      if (response?.items) {
        setTableData(response.items);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedTableData = useMemo(() => {
    if (!tableData?.length || !sortColumn) return tableData;
    return [...tableData].sort((a: any, b: any) => {
      if (sortColumn === "audienceSize") {
        const va = a.differential_privacy_count_total ?? 0;
        const vb = b.differential_privacy_count_total ?? 0;
        return sortDirection === "asc" ? va - vb : vb - va;
      }
      if (sortColumn === "createdAt") {
        const va = new Date(a.created_at).getTime() || 0;
        const vb = new Date(b.created_at).getTime() || 0;
        return sortDirection === "asc" ? va - vb : vb - va;
      }
      return 0;
    });
  }, [tableData, sortColumn, sortDirection]);

  useEffect(() => {
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/campaign-reports")),
      )?.label;

    setActiveSubItem(sideBarMenuToHighlight);
    setIsSideBarMenuDisabled({
      fed: false,
      cp: false,
      cr: false,
      pr: false,
      ah: false,
      ca: false,
      dm: false,
    });
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Federation Selection", path: "/federation-selection" },
      {
        label:
          `${fedSpecificMenuLabels?.campaign_reports}` || `Audience Details`,
        path: "/campaign-reports",
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadReport = async ({ fileFormat }) => {
    try {
      const res = await getCampaignReportsCSVFn.run({
        deferFn: getCampaignReportsCSV,
        federationId: fedId,
        organizationId: orgId,
        role: userRole,
        file_format: fileFormat,
      });

      if (!res.ok) {
        let errorText;
        try {
          errorText = await res.text();
        } catch (parseErr) {
          errorText = "[Unable to read error body]";
        }
        console.error("Status:", res.status);
        console.error("Response body:", errorText);

        console.error("Status:", res.status);
        console.error("Response body:", errorText);
        console.log("Content-Type:", res.headers.get("content-type"));

        throw new Error(`Download failed with status ${res.status}`);
      }

      const blob = await res.blob();

      const formatTimestamp = () => {
        const now = new Date();
        const day = now.getDate().toString().padStart(2, "0");
        const month = now.toLocaleString("default", { month: "short" });
        const year = now.getFullYear().toString().slice(-2);
        let hours = now.getHours();
        const minutes = now.getMinutes().toString().padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${day}${month}${year}_${hours}.${minutes}${ampm}`;
      };

      const contentDisposition = res.headers.get("content-disposition");
      const timestamp = formatTimestamp();
      const safeName =
        localStorage.getItem("federationname")?.replace(/\s+/g, "_") ||
        "Unknown";
      let filename = `CampaignReports_${safeName}_${timestamp}.${fileFormat}`;
      if (contentDisposition?.includes("filename=")) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Error downloading ${fileFormat}:`, err);
    }
  };

  const fetchLimitsUsage = async () => {
    const objectives = allowedQueryObjectives.length
      ? allowedQueryObjectives
      : ["activation"];
    const results = await fetchLimitsUsageData(objectives, {
      organizationId: orgId,
      federationId: fedId,
      ...(filterMatchId ? { matchId: filterMatchId } : {}),
    });
    setLimitsUsageResults(results);
  };

  const openLimitsUsagePopup = () => {
    setShowLimitsUsagePopup(true);
    fetchLimitsUsage();
  };

  // FR-20 Task 2 AC4/AC5: the Engage button needs activation usage
  // proactively (not only when the limits popup is opened) to show
  // "current/limit" and disable itself once the limit is reached, so fetch
  // it once up front instead of piggybacking on openLimitsUsagePopup.
  useEffect(() => {
    if (orgId && fedId) fetchLimitsUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, fedId, filterMatchId]);

  const activationUsageByObjective = useMemo(() => {
    const map: Record<string, { activation_count: number; activation_limit: number }> = {};
    limitsUsageResults.forEach(({ objective, usage }) => {
      if (objective && usage?.activation_usage) map[objective] = usage.activation_usage;
    });
    return map;
  }, [limitsUsageResults]);

  // Some rows never carry a real objective string worth keying usage on:
  // AskTamarai's NL-query flow submits "audienceTargeting", and compound
  // queries (is_compound_query: true) come back with objective: "" - both
  // are tracked under the "activation" usage bucket, not a bucket of their
  // own, so normalize the lookup key or these rows' Engage/Engaged buttons
  // never find usage.
  const getActivationUsageForRow = (row) => {
    const rawObjective = row?.objective;
    const usageKey =
      !rawObjective || rawObjective === "audienceTargeting"
        ? "activation"
        : rawObjective;
    return activationUsageByObjective[usageKey];
  };

  const getEngagedLabel = (row, suffix = "") => {
    return `Engaged${suffix}`;
  };

  const isEngageBlockedByActivationLimit = (row) => {
    const usage = getActivationUsageForRow(row);
    return !!usage && usage.activation_count >= usage.activation_limit;
  };

  const getEngageLabel = (row) => {
    if (isEngageBlockedByActivationLimit(row)) return "Limit Reached";
    return "Engage";
  };

  const handleDeleteQuery = async (row, rowIndex) => {
    const QueryId = row.id;
    setRowIcons((prev) => ({
      ...prev,
      [rowIndex]: getMarkButtonText({ deleting: true }),
    }));

    try {
      setLoading(true);
      await DeletequeryFn.run({ QueryId, organizationId: orgId });
      getQueriesFn.run();
      getRunningQueriesFn.run();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  var multiarr;
  var subRow;
  var multilist = [{ IsActive: "" }];
  var IsActive;
  var totalCount;
  var subtableRow;
  if (multipleIdData !== undefined && multipleIdData !== null) {
    for (let i = 0; i < multipleIdData.length; i++) {
      multiarr = multipleIdData[i];
      const countrate = multiarr.differential_privacy_count_total;
      const countDisplay = countrate.toLocaleString("en-US");
      totalCount = countDisplay.toLocaleString("en-US");
      const update = multiarr.updated_at;
      const identifier = multiarr.identifier;
      const bucketName = multiarr.inquirer_bucket_name;
      const objective = multiarr.objective;
      const audience_enrichment_count =
        multiarr.attribute_enriched_record_count;
      if (
        objective === "enrichment" &&
        audience_enrichment_count !== undefined
      ) {
        const audienceCount = audience_enrichment_count.toLocaleString("en-US");

        totalCount = audienceCount;
      }

      const overallstatus = multipleIdData[i].status;
      const isDeleted = multipleIdData[i].is_deleted;
      const orgName =
        multipleIdData[i].inquirer_organization_name || organization_name;

      if (overallstatus === "FAILED") {
        totalCount = "0";
      }
      var subQueryId;
      var subUpdated_at;
      var totalSubCount;
      subtableRow = multiarr.executed_queries;
      var submultilist = [{ IsActive: "" }];
      if (overallstatus === "SUCCEEDED" && subtableRow !== null) {
        for (let k = 0; k < subtableRow.length; k++) {
          subRow = subtableRow[k];
          subQueryId = subRow.subquery_id;
          subUpdated_at = subRow.last_status_update;
          const subBucketName = subRow.contributor_bucket_name;
          const subOrgName = subRow.contributor_organization_name;
          const AudienceCount =
            subRow.differential_privacy_query?.type === "DP"
              ? subRow.differential_privacy_query.result_count
              : subRow.audience_result_count;
          const subAudienceCount = AudienceCount.toLocaleString("en-US");
          const submultiex = {
            countDisplay: "",
            QueryId: "",
            IsActive: "",
            overallstatus: "",
            iamCountDisplay: "",
            updated_at: "",
            identifier: "",
            bucketname: "",
            subOrgName: "",
          };
          totalSubCount = subAudienceCount;
          if (objective === "enrichment") {
            totalSubCount = "NA";
          }
          Object.assign(submultiex, {
            countDisplay: totalSubCount,
            QueryId: subQueryId,
            IsActive: "subRow",
            overallstatus: overallstatus,
            iamCountDisplay: 0,
            updated_at: subUpdated_at,
            identifier: identifier,
            bucketname: subBucketName + " | " + subOrgName,
            subOrgName: subOrgName,
          });
          submultilist.push(submultiex);
        }
      }
      submultilist.shift();

      var multiex = {
        countDisplay: "",
        objective: "",
        QueryId: "",
        IsActive: "",
        overallstatus: "",
        isDeleted: "",
        updated_at: "",
        identifier: "",
        bucketname: "",
        inquirerorgName: "",
        executedQueries: [
          {
            countDisplay: "",
            subQueryId: "",
            IsActive: "",
            overallstatus: "",
            updated_at: "",
            identifier: "",
            bucketname: "",
          },
        ],
      };
      const QueryId = multipleIdData[i]?.id;
      IsActive = multipleIdData[i].marked_for_activation;
      Object.assign(multiex, {
        countDisplay: totalCount,
        QueryId: QueryId,
        objective: objective,
        IsActive: IsActive.toString(),
        overallstatus: overallstatus,
        isDeleted: isDeleted,
        iamCountDisplay: 0,
        updated_at: update,
        identifier: identifier,
        bucketname: bucketName,
        inquirerorgName: orgName,
        executedQueries: submultilist,
      });
      multilist.push(multiex);
    }
    multilist.shift();
  }
  useEffect(() => {
    if (!loading) {
      setRefreshIn(15);
    }
  }, [loading]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        loading ||
        openSearchQueryIDModal ||
        datePickerOpen ||
        showActivationPopup ||
        openQueryIdPopup ||
        openFailedInfoPopup ||
        autoRefreshPaused ||
        showDeletePopup ||
        openMatchIDDropdown ||
        openParticipantsDropdown ||
        !!breakdownModalData ||
        isOverviewOpen
      )
        return;

      setRefreshIn((prev) => {
        if (prev === 1) {
          reloadQueries("AUTO_REFRESH");
          return 15;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    openSearchQueryIDModal,
    datePickerOpen,
    showActivationPopup,
    openQueryIdPopup,
    openFailedInfoPopup,
    autoRefreshPaused,
    showDeletePopup,
    openMatchIDDropdown,
    openParticipantsDropdown,
    breakdownModalData,
    isOverviewOpen,
  ]);

  useEffect(() => {
    if (skipNextTableDataSync.current) {
      skipNextTableDataSync.current = false;
      return;
    }
    setTableData(getQueriesFn.data?.items);
  }, [getQueriesFn.data]);

  useEffect(() => {
    if (!highlightedRowId) return;
    const match = tableData?.find((row: any) => row.id === highlightedRowId);
    if (!match) return;
    const timer = setTimeout(() => setHighlightedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [tableData, highlightedRowId]);

  useEffect(() => {
    tableData?.forEach((query) => {
      if (
        query.status === "RUNNING" ||
        query.status === "PROCESSING" ||
        query.status === "processing"
      ) {
        updateProgress(query.id, query.completion_percentage);
      }
    });
  }, [tableData]);

  useEffect(() => {
    const tableContainer = document.querySelector(
      `.${styles.container}`,
    ) as HTMLElement;
    if (tableContainer) {
      tableContainer.style.minHeight =
        openMatchIDDropdown || openParticipantsDropdown ? "500px" : "";
    }
  }, [openParticipantsDropdown, openMatchIDDropdown]);

  const getOrgByFederationIDFn = useAsyncFn({
    promiseFn: getOrgByFederationID,
    federationID: sessionFederationId,
  });
  useEffect(() => {
    if (
      getQueriesFn.error ||
      getRunningQueriesFn.error ||
      getOrgByFederationIDFn.error ||
      getConfigFn.error
    ) {
      setFailureMessage("Something went wrong. Please try again later.");
    }
  }, [
    getQueriesFn.error,
    getRunningQueriesFn.error,
    getOrgByFederationIDFn.error,
    getConfigFn.error,
  ]);
  var allOrganizationsWithFeds =
    getOrgByFederationIDFn.data && getOrgByFederationIDFn.data.items;
  const allOrgs = allOrganizationsWithFeds?.map((item) => item.organization);

  var FilterQueryId;
  var campaignName;
  campaignName = filterCampaignName;
  FilterQueryId = filter_query;


  const formatTimestamp = (timestamp) => {
    return timestamp.replace(/\.000Z$/, "Z");
  };

  const runOverviewFetch = (start: string, end: string, cursor?: string) => {
    return getOverviewQueriesFn.run({
      promiseFn: getquery,
      federationId: fedId,
      organizationId: orgId,
      page_size: 20,
      scan_index_forward: false,
      include_child_queries: true,
      nl_query: false,
      role: userRole,
      start_date: formatTimestamp(new Date(start + "T00:00:00").toISOString()),
      last_key: cursor
        ? formatTimestamp(cursor)
        : formatTimestamp(new Date(end + "T23:59:59").toISOString()),
      exclude_zero_count: true,
      exclude_failed: true,
    });
  };

  const openOverview = () => {
    const { start, end } = getLastMonthRange();
    const startStr = toDateValue(start);
    const endStr = toDateValue(end);
    overviewStartRef.current = startStr;
    overviewIsLoadMoreRef.current = false;
    setOverviewRows([]);
    setOverviewHasMore(false);
    runOverviewFetch(startStr, endStr);
    setIsOverviewOpen(true);
  };

  const handleOverviewDateChange = (start: string, end: string) => {
    overviewStartRef.current = start;
    overviewIsLoadMoreRef.current = false;
    setOverviewRows([]);
    setOverviewHasMore(false);
    runOverviewFetch(start, end);
  };

  const handleOverviewLoadMore = () => {
    const lastRow = overviewRows[overviewRows.length - 1];
    if (!lastRow || overviewIsLoadingMore) return;
    overviewIsLoadMoreRef.current = true;
    setOverviewIsLoadingMore(true);
    runOverviewFetch(overviewStartRef.current, "", lastRow.created_at);
  };

  // Accumulate overview pages; append on load-more, replace on fresh fetch
  useEffect(() => {
    if (!getOverviewQueriesFn.isFulfilled) return;
    const items: any[] = getOverviewQueriesFn.data?.items ?? [];
    if (overviewIsLoadMoreRef.current) {
      setOverviewRows((prev) => [...prev, ...items]);
      setOverviewHasMore(items.length === 20);
      setOverviewIsLoadingMore(false);
      overviewIsLoadMoreRef.current = false;
    } else {
      setOverviewRows(items);
      setOverviewHasMore(items.length === 20);
    }
  }, [getOverviewQueriesFn.isFulfilled, getOverviewQueriesFn.data]);

  // Without this, a failed overview fetch leaves overviewIsLoadingMore stuck
  // true forever — the modal has no other path back to a non-loading state.
  useEffect(() => {
    if (!getOverviewQueriesFn.error) return;
    overviewIsLoadMoreRef.current = false;
    setOverviewIsLoadingMore(false);
    setOverviewHasMore(false);
    showToast("Failed to load audience overview data.", "error");
  }, [getOverviewQueriesFn.error]);

  const HandleFilters = async (value) => {
    setIsLastPage(false);
    setCurrentPage(1);
    currentPageKey.current = undefined;
    pageDepth.current = 0;
    setFilterApplied(true);
    const isQueryId = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(
      value,
    );
    if (isQueryId) FilterQueryId = value;
    else campaignName = value;
    setCampaignName(campaignName);
    setFilterQueryId(FilterQueryId);
    const tempFilterVal = { ...filterValue };
    delete tempFilterVal["Query"];
    delete tempFilterVal["Campaign"];
    setFilterValue({
      ...tempFilterVal,
      ...(isQueryId ? { Query: value } : { Campaign: value }),
    });
    setLoading(true);
    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        organizationId: orgId,
        contributor_org_id: allOrgs?.find(
          (item) => item.name === filterParticipant,
        )?.organization_id,
        ...(campaignName && { campaign_name: campaignName }),
        ...(FilterQueryId && { query_id: FilterQueryId }),
        page_size: rowsPerPage,
        role: userRole,
        Identifier: filterMatchId,
        include_child_queries: true,
        nl_query: false,
        start_date: filterStartDate
          ? formatTimestamp(new Date(filterStartDate).toISOString())
          : undefined,
        last_key: filterEndDate
          ? formatTimestamp(new Date(filterEndDate).toISOString())
          : undefined,
      });

      if (response?.items) {
        setTableData(response.items);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // CHANGED: added setLoading(false) in finally so shimmer dismisses correctly
  // after every reload, including the manual Reload button click
  const reloadQueries = async (type?: string) => {
    setIsLastPage(false);
    setLoading(true);
    triggerForQueryPercentageFn.run().catch(() => {});
    fetchLimitsUsage().catch(() => {});
    const defaultParams = {
      promiseFn: getquery,
      federationId: fedId,
      organizationId: orgId,
      contributor_org_id: allOrgs?.find(
        (item) => item.name === filterParticipantRef.current,
      )?.organization_id,
      page_size: rowsPerPage,
      role: userRole,
      query_id: filterQueryIdRef.current,
      campaign_name: filterCampaignNameRef.current,
      Identifier: filterMatchIdRef.current,
      nl_query: false,
      include_child_queries: true,
    };
    const usePaginationParamsForAutoRefresh =
      type === "AUTO_REFRESH" && pageDepth.current > 0;

    const params = {
      ...(usePaginationParamsForAutoRefresh
        ? {
            ...defaultParams,
            scan_index_forward:
              currentPageKey.current?.scan_index_forward || false,
            start_date: filterStartDateRef.current
              ? formatTimestamp(new Date(filterStartDateRef.current).toISOString())
              : undefined,
            last_key: currentPageKey.current
              ? formatTimestamp(currentPageKey.current?.key)
              : undefined,
          }
        : {
            ...defaultParams,
            scan_index_forward: false,
            last_key: FilterEndDateRef.current
              ? formatTimestamp(new Date(FilterEndDateRef.current).toISOString())
              : undefined,
            start_date: filterStartDateRef.current
              ? formatTimestamp(new Date(filterStartDateRef.current).toISOString())
              : undefined,
          }),
    };

    try {
      const response = await getQueriesFn.run(params);

      if (Array.isArray(response?.items)) {
        if (response.items.length > 0) {
          setTableData(response.items);
          if (type !== "AUTO_REFRESH") {
            currentPageKey.current = undefined;
            pageDepth.current = 0;
            setCurrentPage(1);
          }
        } else if (type !== "AUTO_REFRESH") {
          setTableData(response.items);
          currentPageKey.current = undefined;
          pageDepth.current = 0;
          setCurrentPage(1);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const ActivequeryFn = useAsyncFn({
    deferFn: updatequery,
    reloadQueries,
  });

  const DeletequeryFn = useAsyncFn({
    deferFn: deletequery,
    reloadQueries,
  });

  useEffect(() => {
    if (ActivequeryFn.data) {
      getQueriesFn.run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ActivequeryFn.data]);

  const putqueryFn = useAsyncFn({
    deferFn: putquery,
  });

  // const parseFilterStrToFilters = (filterStrArr: string[]) => {
  //   if (!filterStrArr?.length) return [];
  //   const operatorMap: Record<string, string> = {
  //     ">=": "gte",
  //     "<=": "lte",
  //     "!=": "neq",
  //     "<>": "neq",
  //     ">": "gt",
  //     "<": "lt",
  //     "=": "eq",
  //   };
  //   return filterStrArr.reduce((acc, str) => {
  //     if (!str?.trim()) return acc;
  //     const match = str.match(/^(\w+)\s*(>=|<=|!=|<>|>|<|=)\s*'?([^']*)'?\s*$/);
  //     if (!match) return acc;
  //     const [, attribute, opStr, value] = match;
  //     acc.push({
  //       attribute,
  //       conditions: [{ operator: operatorMap[opStr] ?? "eq", value }],
  //     });
  //     return acc;
  //   }, [] as object[]);
  // };

  // const handleRetryQuery = async (row) => {
  //   const { given_name, family_name } = user || {};
  //   const user_name = `${given_name ?? ""} ${family_name ?? ""}`.trim();
  //   const body = {
  //     user_name,
  //     federation_id: sessionFederationId,
  //     identifier: row.identifier,
  //     inquirer_organization_id: orgId,
  //     inquirer_organization_name: row.inquirer_organization_name,
  //     inquirer_bucket_id: "",
  //     inquirer_bucket_name: row.inquirer_bucket_name,
  //     contributor_bucket: row.contributor_bucket,
  //     contributor_organization_ids: row.contributor_organization_ids,
  //     attributes_selected: row.attributes_selected,
  //     filters: row.filters?.length
  //       ? row.filters
  //       : parseFilterStrToFilters(row.filter_str),
  //     filter_str: row.filter_str,
  //     campaign_name: row.campaign_name,
  //     campaign_id: row.campaign_id,
  //     description: row.description,
  //     override_empty_predicate: false,
  //     objective: row.objective,
  //     nl_query: row.nl_query,
  //   };
  //   await putqueryFn.run({
  //     body,
  //     fedId: sessionFederationId,
  //     orgId,
  //     intermediate: true,
  //   });
  // };

  const putqueryAIFn = useAsyncFn({
    deferFn: putqueryAI,
  });
  var finalStart;

  // CHANGED: wrapped in setLoading(true) / finally setLoading(false)
  // so shimmer shows during pagination API calls too
  const handleNextPageClick = async () => {
    if (!tableData?.length || tableData.length < rowsPerPage || isLastPage) {
      showToast("No more data to display.", "info");
      return;
    }
    pageDepth.current++;
    setLoading(true);
    const lastCreatedAt =
      Array.isArray(tableData) && tableData.length > 0
        ? tableData[tableData.length - 1]?.created_at
        : undefined;
    prevPageTableDataRef.current = [...tableData];
    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        page_size: rowsPerPage,
        scan_index_forward: false,
        start_date: filterStartDateRef.current
          ? formatTimestamp(new Date(filterStartDateRef.current).toISOString())
          : finalStart || undefined,
        last_key:
          Array.isArray(tableData) &&
          tableData.length > 0 &&
          tableData[tableData.length - 1]
            ? formatTimestamp(tableData[tableData.length - 1].created_at)
            : undefined,
        role: userRole,
        query_id: filterQueryId,
        campaign_name: filterCampaignName,
        organizationId: orgId,
        contributor_org_id: allOrgs?.find(
          (item) => item.name === filterParticipant,
        )?.organization_id,
        Identifier: filterMatchId,
        include_child_queries: true,
        nl_query: false,
      });
      if (response?.items?.length) {
        currentPageKey.current = {
          key: lastCreatedAt,
          scan_index_forward: false,
        };
        setTableData(response.items);
        setCurrentPage((prev) => prev + 1);
      } else {
        pageDepth.current--;
        skipNextTableDataSync.current = true;
        setTableData(prevPageTableDataRef.current);
        setIsLastPage(true);
        showToast("No more records available.", "info");
      }
    } finally {
      setLoading(false);
    }
  };

  function sanitizeClassName(input: string): string {
    return input
      ?.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  const isQueryExpired = (row) =>
    !!(row.expiry_duration && new Date() >= new Date(row.expiry_duration));

  const getDaysUntilExpiry = (row: any): number | null => {
    if (!row.expiry_duration) return null;
    const expiry = new Date(row.expiry_duration);
    if (isNaN(expiry.getTime())) return null;
    const diff = expiry.getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getStatusLabel = (status: string): string => {
    switch (status?.toUpperCase()) {
      case "SUCCEEDED": return "Succeeded";
      case "FAILED": return "Failed";
      case "CANCELLED": return "Cancelled";
      case "RUNNING":
      case "PROCESSING":
      case "PENDING": return "Running";
      default: return status ?? "";
    }
  };

  const removeFilter = async (key: string) => {
    const newFilterValue = { ...filterValue };
    delete newFilterValue[key];
    setFilterValue(newFilterValue);
    if (Object.keys(newFilterValue).length === 0) setFilterApplied(false);

    let newMatchId = filterMatchId;
    let newCampaignName = filterCampaignName;
    let newQueryId = filterQueryId;
    let newParticipant = filterParticipant;
    let newStartDate = filterStartDate;
    let newEndDate = filterEndDate;

    if (key === "Query" || key === "Campaign") {
      newCampaignName = undefined;
      newQueryId = undefined;
      setCampaignName(undefined);
      setFilterQueryId(undefined);
    }
    if (key === "ID Type") {
      newMatchId = undefined;
      setMatchId(undefined);
    }
    if (key === "Created At") {
      newStartDate = undefined;
      newEndDate = undefined;
      setFilterStart(undefined);
      setFilterEnd(undefined);
    }
    if (key === "Organization") {
      newParticipant = undefined;
      setFilterParticipant(undefined);
    }

    setIsLastPage(false);
    setCurrentPage(1);
    currentPageKey.current = undefined;
    pageDepth.current = 0;
    setLoading(true);
    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        organizationId: orgId,
        contributor_org_id: allOrgs?.find((item) => item.name === newParticipant)?.organization_id,
        page_size: rowsPerPage,
        role: userRole,
        query_id: newQueryId,
        campaign_name: newCampaignName,
        Identifier: newMatchId,
        include_child_queries: true,
        nl_query: false,
        start_date: newStartDate ? formatTimestamp(new Date(newStartDate).toISOString()) : undefined,
        last_key: newEndDate ? formatTimestamp(new Date(newEndDate).toISOString()) : undefined,
      });
      if (response?.items) setTableData(response.items);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMarkButtonText = (row) => {
    let actionText;
    if (row.deleting) actionText = "";
    if (row.is_deleted && row.marked_for_activation)
      actionText = "Engaged & Deleted";
    else if (row.status === "RUNNING") actionText = "Cancel";
    else if (row.marked_for_activation) {
      actionText = "Engaged";
    } else if (row.status === "CANCELLED") {
      actionText = "Cancelled";
    } else if (row.status === "FAILED") actionText = "Failed";
    else if (row.is_deleted) actionText = "Deleted";
    else if (
      row.status === "SUCCEEDED" &&
      row.differential_privacy_count_total > 0
    ) {
      if (isQueryExpired(row)) {
        actionText = "Expired";
      } else {
        actionText = !readAccess ? "Engage" : "";
      }
    }
    return actionText;
  };

  // CHANGED: wrapped in setLoading(true) / finally setLoading(false)
  // so shimmer shows during pagination API calls too
  const handlePreviousPageClick = async () => {
    if (pageDepth.current === 0) {
      showToast("You are already on the first page.", "info");
      return;
    }

    setIsLastPage(false);
    pageDepth.current--;
    if (
      currentPage - 1 === visiblePages[visiblePages.length - 1] &&
      currentPage < 10
    ) {
      setVisiblePages([...visiblePages, currentPage + 1]);
    } else if (currentPage - 1 === visiblePages[0] && currentPage - 1 > 1) {
      setVisiblePages([currentPage - 2, ...visiblePages]);
    }
    setLoading(true);
    const prevFirstCreatedAt =
      Array.isArray(tableData) && tableData.length > 0
        ? tableData[0]?.created_at
        : undefined;
    const lastKey =
      Array.isArray(tableData) && tableData.length > 0 && tableData[0]
        ? tableData[0].created_at
        : undefined;

    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        organizationId: orgId,
        contributor_org_id: allOrgs?.find(
          (item) => item.name === filterParticipantRef.current,
        )?.organization_id,
        page_size: rowsPerPage,
        scan_index_forward: true,
        role: userRole,
        include_child_queries: true,
        nl_query: false,
        query_id: filterQueryIdRef.current,
        campaign_name: filterCampaignNameRef.current,
        Identifier: filterMatchIdRef.current,
        start_date: filterStartDateRef.current
          ? formatTimestamp(new Date(filterStartDateRef.current).toISOString())
          : finalStart || undefined,
        last_key: lastKey ? formatTimestamp(lastKey) : undefined,
      });
      if (response?.items?.length) {
        currentPageKey.current = {
          key: prevFirstCreatedAt,
          scan_index_forward: true,
        };
        setTableData(response.items);
        setCurrentPage((prev) => prev - 1);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (putqueryFn.isFulfilled && putqueryFn.data && putqueryFn.data?.id) {
      getQueriesFn.run({
        federationId: fedId,
        organizationId: orgId,
        page_size: rowsPerPage,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
        nl_query: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    putqueryFn.isFulfilled,
    putqueryFn.data,
    putqueryAIFn.isFulfilled,
    putqueryAIFn.data,
  ]);

  const getSelectedMatchID = async (value) => {
    setIsLastPage(false);
    setCurrentPage(1);
    currentPageKey.current = undefined;
    pageDepth.current = 0;
    setOpenMatchIDDropdown(false);
    setFilterApplied(true);
    setFilterValue({ ...filterValue, "ID Type": identityTypeMap.get(value) ?? value });
    setMatchId(value);
    setLoading(true);
    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        organizationId: orgId,
        contributor_org_id: allOrgs?.find(
          (item) => item.name === filterParticipant,
        )?.organization_id,
        page_size: rowsPerPage,
        role: userRole,
        query_id: filterQueryId,
        campaign_name: filterCampaignName,
        Identifier: value,
        include_child_queries: true,
        nl_query: false,
        start_date: filterStartDate
          ? formatTimestamp(new Date(filterStartDate).toISOString())
          : undefined,
        last_key: filterEndDate
          ? formatTimestamp(new Date(filterEndDate).toISOString())
          : undefined,
      });

      if (response?.items) {
        setTableData(response.items);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const HandleTimestamp = async (startDate, endDate) => {
    const startDateObj = startDate?.replace("T", " ");
    const endDateObj = endDate?.replace("T", " ");
    setIsLastPage(false);
    setCurrentPage(1);
    currentPageKey.current = undefined;
    pageDepth.current = 0;
    setFilterApplied(true);
    setFilterValue({
      ...filterValue,
      "Created At": `From ${startDateObj} to ${endDateObj}`,
    });
    setFilterStart(startDate);
    setFilterEnd(endDate);
    setLoading(true);
    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        organizationId: orgId,
        Identifier: filterMatchId,
        contributor_org_id: allOrgs?.find(
          (item) => item.name === filterParticipant,
        )?.organization_id,
        page_size: rowsPerPage,
        query_id: FilterQueryId,
        campaign_name: filterCampaignName,
        include_child_queries: true,
        nl_query: false,
        start_date: formatTimestamp(new Date(startDate).toISOString()),
        last_key: formatTimestamp(new Date(endDate).toISOString()),
        role: userRole,
      });

      if (response?.items) {
        setTableData([...response.items]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const getSelectedParticipant = async (value) => {
    setIsLastPage(false);
    setCurrentPage(1);
    currentPageKey.current = undefined;
    pageDepth.current = 0;
    setOpenParticipantsDropdown(false);
    setFilterValue({ ...filterValue, Organization: value });
    setFilterApplied(true);
    setFilterParticipant(value);
    setLoading(true);
    try {
      const response = await getQueriesFn.run({
        promiseFn: getquery,
        federationId: fedId,
        organizationId: orgId,
        contributor_org_id: allOrgs?.find((item) => item.name === value)
          ?.organization_id,
        page_size: rowsPerPage,
        role: userRole,
        query_id: filterQueryId,
        campaign_name: filterCampaignName,
        Identifier: filterMatchId,
        include_child_queries: true,
        nl_query: false,
        start_date: filterStartDate
          ? formatTimestamp(new Date(filterStartDate).toISOString())
          : undefined,
        last_key: filterEndDate
          ? formatTimestamp(new Date(filterEndDate).toISOString())
          : undefined,
      });

      if (response?.items) {
        setTableData(response.items);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const getStatusIcon = (status) => {
    let icon;
    switch (status) {
      case "SUCCEEDED": {
        icon = DoneIcon;
        break;
      }
      case "FAILED": {
        icon = DeletedIcon;
        break;
      }
      case "CANCELLED": {
        icon = CancelledIcon;
        break;
      }
      case "RUNNING": {
        icon = RunningIcon;
        break;
      }
      case "PROCESSING": {
        icon = RunningIcon;
        break;
      }
      case "processing": {
        icon = RunningIcon;
        break;
      }
      case "PENDING": {
        icon = RunningIcon;
        break;
      }
    }
    return icon;
  };
  const goBack = () => {
    if (filterApplied) {
      setFilterApplied(false);
      ClearFilters();
    } else {
      window?.history?.back();
    }
  };

  return storedPermissions?.query === "no" ? (
    <PageNotFound />
  ) : (
    <LayoutContainer>
      {errorMessage && (
        <InfoPopup
          displayText={errorMessage}
          freeText
          isOpen={errorMessage !== ""}
          onClose={() => setErrorMessage("")}
          headerText="Information!"
        />
      )}
      {showSuccessPopup && (
        <InfoPopup
          isOpen={showSuccessPopup !== null}
          title="Engagement Results"
          onClose={() => {
            setShowSuccessPopup(null);
            reloadQueries();
          }}
        >
          {(showSuccessPopup || "Initiated Action!")
            .split("\n")
            .filter(Boolean)
            .map((line, i) => {
              const lower = line.toLowerCase();
              const isSuccess = lower.startsWith("success");
              const isNoop =
                lower.startsWith("no-op") || lower.startsWith("noop");
              const isError = lower.startsWith("error");
              const bg = isSuccess
                ? "#f0fdf4"
                : isNoop
                  ? "#fffbeb"
                  : isError
                    ? "#fef2f2"
                    : "#f8fafc";
              const dotColor = isSuccess
                ? "#080909"
                : isNoop
                  ? "#d97706"
                  : isError
                    ? "#dc2626"
                    : "#64748b";
              const labelColor = isSuccess
                ? "#15803d"
                : isNoop
                  ? "#b45309"
                  : isError
                    ? "#b91c1c"
                    : "#475569";
              const [prefix, ...rest] = line.split("·");
              const body = rest.join("·").trim();
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    background: bg,
                    border: `1px solid ${dotColor}22`,
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 8,
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: dotColor,
                      flexShrink: 0,
                      marginTop: 5,
                    }}
                  />
                  <div
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: "#1a202c",
                      lineHeight: 1.5,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: labelColor,
                        marginRight: 6,
                      }}
                    >
                      {prefix.trim()}
                    </span>
                    {body}
                  </div>
                </div>
              );
            })}
        </InfoPopup>
      )}
      {showFailurePopup && (
        <InfoPopup
          isOpen={!!showFailurePopup}
          onClose={() => {
            setShowFailurePopup(null);
          }}
          headerText="Information!"
          displayText={
            typeof showFailurePopup === "string"
              ? showFailurePopup
              : "Something went wrong. Please re-check inputs and try again."
          }
          freeText
        />
      )}
      {showActivationPopup && (
        <ActivationPopup
          onClose={() => setShowActivationPopup(false)}
          queryData={showActivationPopup}
          onSuccess={(results) => {
            setShowSuccessPopup(results);
            fetchLimitsUsage();
          }}
          onFailure={(message?: string) => {
            setShowFailurePopup(message || true);
          }}
        />
      )}

      {showDeletePopup && (
        <InfoPopup
          isOpen={!!showDeletePopup}
          onClose={() => {
            if (showDeletePopup) {
              handleDeleteQuery(showDeletePopup.row, showDeletePopup.index);
            }
            setShowDeletePopup(null);
          }}
          headerText="Information"
          displayText={
            showDeletePopup.actionType === "cancel"
              ? "Are you sure you want to cancel this query?"
              : "Are you sure you want to delete this query?"
          }
          showCancelButton
          closeButtonLabel="Confirm"
          onCancelButtonClick={() => setShowDeletePopup(null)}
          freeText
        />
      )}

      {publishCohortRow && (
        <InfoPopup
          isOpen={true}
          onClose={cancelPublishCohortDialog}
          title="Publish Cohort"
          contentWidth="min(480px, 92vw)"
        >
          <div
            className={styles.publishCohortDialog}
            onKeyDown={(e) => {
              if (e.key !== "Escape") return;
              if (cohortDropdownOpen) {
                setCohortDropdownOpen(false);
                setCohortActiveIndex(-1);
              } else {
                cancelPublishCohortDialog();
              }
            }}
          >
            <div className={styles.publishCohortField}>
              <label
                className={styles.publishCohortLabel}
                htmlFor="publish-cohort-name"
              >
                Category <span className={styles.publishCohortRequired}>*</span>
              </label>
              <input
                id="publish-cohort-name"
                type="text"
                role="combobox"
                aria-expanded={filteredCohortCategories.length > 0}
                aria-controls="publish-cohort-listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  cohortActiveIndex >= 0
                    ? `publish-cohort-option-${cohortActiveIndex}`
                    : undefined
                }
                autoComplete="off"
                className={styles.publishCohortInput}
                placeholder="Search or enter a category"
                value={cohortNameInput}
                onChange={handleCohortNameChange}
                onKeyDown={handleCohortNameKeyDown}
                onFocus={() =>
                  setCohortDropdownOpen(cohortNameInput.length > 0)
                }
                onBlur={() => {
                  setCohortDropdownOpen(false);
                  setCohortActiveIndex(-1);
                }}
                maxLength={40}
                autoFocus
              />
              {filteredCohortCategories.length > 0 && (
                <div
                  id="publish-cohort-listbox"
                  role="listbox"
                  className={styles.publishCohortDropdown}
                >
                  {filteredCohortCategories.map((category, idx) => (
                    <div
                      key={category.id}
                      id={`publish-cohort-option-${idx}`}
                      role="option"
                      aria-selected={idx === cohortActiveIndex}
                      className={`${styles.publishCohortDropdownItem}${idx === cohortActiveIndex ? ` ${styles.publishCohortDropdownItemActive}` : ""}`}
                      // mousedown (not click) so the input's blur doesn't
                      // close the list before the selection registers
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectCohortCategory(category);
                      }}
                    >
                      {category.name}
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.publishCohortMeta}>
                {cohortNameError ? (
                  <span className={styles.publishCohortError} role="alert">
                    {cohortNameError}
                  </span>
                ) : cohortNameInput.trim() ? (
                  <span className={styles.publishCohortHint}>
                    {matchedCohortCategory
                      ? "Existing category will be used"
                      : "A new category will be created"}
                  </span>
                ) : (
                  <span />
                )}
                <span
                  className={`${styles.publishCohortCount}${cohortNameInput.length >= 36 ? ` ${styles.publishCohortCountWarn}` : ""}`}
                >
                  {cohortNameInput.length} / 40
                </span>
              </div>
            </div>
            <div className={styles.publishCohortField}>
              <label
                className={styles.publishCohortLabel}
                htmlFor="publish-cohort-description"
              >
                Description
              </label>
              <textarea
                id="publish-cohort-description"
                className={styles.publishCohortTextarea}
                rows={3}
                placeholder="What is this category for?"
                value={matchedCohortCategory ? "" : cohortDescriptionInput}
                onChange={handleCohortDescriptionChange}
                disabled={!!matchedCohortCategory}
              />
              <div className={styles.publishCohortMeta}>
                {cohortDescriptionError ? (
                  <span className={styles.publishCohortError} role="alert">
                    {cohortDescriptionError}
                  </span>
                ) : matchedCohortCategory ? (
                  <span className={styles.publishCohortHint}>
                    Only used when creating a new category
                  </span>
                ) : (
                  <span />
                )}
                <span
                  className={`${styles.publishCohortCount}${cohortDescriptionInput.length >= 90 ? ` ${styles.publishCohortCountWarn}` : ""}`}
                >
                  {cohortDescriptionInput.length} / 100
                </span>
              </div>
            </div>
            <div className={styles.publishCohortActions}>
              <button
                className={styles.publishCohortCancelBtn}
                onClick={cancelPublishCohortDialog}
                disabled={cohortPublishLoading === publishCohortRow.id}
              >
                Cancel
              </button>
              <button
                className={styles.queryDetailPublishBtn}
                onClick={publishCohort}
                disabled={!canPublishCohort}
              >
                {cohortPublishLoading === publishCohortRow.id
                  ? "..."
                  : "Publish"}
              </button>
            </div>
          </div>
        </InfoPopup>
      )}

      {showCampaignVolumesModal && (
        <CampaignVolumesModal
          data={getCampaignVolumesFn.data?.items ?? []}
          loading={getCampaignVolumesFn.isPending}
          error={
            getCampaignVolumesFn.isRejected
              ? "Failed to load audience volumes."
              : null
          }
          onClose={() => setShowCampaignVolumesModal(false)}
        />
      )}

      {breakdownModalData && (
        <QueryBreakdownModal
          isOpen={!!breakdownModalData}
          onClose={() => setBreakdownModalData(null)}
          data={breakdownModalData}
        />
      )}

      <AudienceOverviewModal
        isOpen={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
        rows={overviewRows}
        isLoading={getOverviewQueriesFn.isPending && !overviewIsLoadingMore}
        hasMore={overviewHasMore}
        isLoadingMore={overviewIsLoadingMore}
        onDateChange={handleOverviewDateChange}
        onLoadMore={handleOverviewLoadMore}
        campaignOptions={getCampaignVolumesFn.data?.items ?? []}
      />

      <div className={styles.pageRoot} ref={pageRootRef}>
        <div className={styles.actionsBar} ref={actionsBarRef}>
          <div className={styles.actionsBarLeft}>
            <button className={styles.backButton} onClick={goBack}>
              <img src={SimpleBackIcon} alt="back" />
              <span className={styles.backButtonText}>Back</span>
            </button>
          </div>
          <div className={styles.actionsBarCenter}>
            {!readAccess && !writeAccess && (
              <>
                <button
                  className={styles.createButton}
                  onClick={() => history.push("/campaign-planning")}
                >
                  + Create Audience
                </button>
                {enableQueryAggregations && (
                  <Tooltip
                    text={
                      selectedItems.length === 1
                        ? "Please select at least 2 queries"
                        : undefined
                    }
                    placement="top"
                  >
                    <button
                      onClick={() =>
                        history.push("/nested-queries", { selectedItems })
                      }
                      className={`${styles.triggerButton}${selectedItems.length < 2 ? ` ${styles.triggerButtonDisabled}` : ""}`}
                      disabled={selectedItems.length < 2}
                    >
                      Trigger Query
                    </button>
                  </Tooltip>
                )}
              </>
            )}
          </div>
          <div className={styles.actionsBarRight}>
            <InforIcon
              className={styles.infoIcon}
              onClick={() => setShowInfoPopup(true)}
            />
            <button
              type="button"
              onClick={() => history.push("/ask-tamarai", { viewOnly: true })}
              className={styles.askTamaraiLink}
            >
              Ask MITRA history →
            </button>
          </div>
          {showInfoPopup && (
            <InfoPopup
              isOpen={showInfoPopup}
              onClose={() => setShowInfoPopup(false)}
              headerText="About this page"
              contentWidth={480}
            >
              <div className={styles.infoContent}>
                <p className={styles.infoIntro}>
                  This page lists every query run in the federation, along
                  with the audience count it produced.
                </p>
                <ul className={styles.infoList}>
                  <li>
                    <span className={styles.infoBullet}>1</span>
                    <span>
                      <strong>Engage</strong> a query to activate its
                      audience, or <strong>delete</strong> it if you no
                      longer need it.
                    </span>
                  </li>
                  <li>
                    <span className={styles.infoBullet}>2</span>
                    <span>
                      Select <strong>2 or more queries</strong> to combine
                      their audiences and trigger a{" "}
                      <strong>nested query</strong>.
                    </span>
                  </li>
                  <li>
                    <span className={styles.infoBullet}>3</span>
                    <span>
                      Use the filters and search above to find queries by
                      status, date, or ID.
                    </span>
                  </li>
                </ul>
                <div className={styles.infoNote}>
                  <span>⏳</span>
                  <span>
                    Queries are automatically deleted once the retention
                    period ends, so engage with any results you need
                    beforehand.
                  </span>
                </div>
              </div>
            </InfoPopup>
          )}
          {showLimitsUsagePopup && (
            <InfoPopup
              isOpen={showLimitsUsagePopup}
              onClose={() => setShowLimitsUsagePopup(false)}
              onRefresh={fetchLimitsUsage}
              isLoading={limitsUsageLoading}
              headerText="Query & Engagement Limits"
              contentWidth={560}
            >
              <div className={styles.limitsUsageContent}>
                {!limitsUsageLoading && limitsUsageResults.length === 0 && (
                  <p>No limits data available.</p>
                )}
                {limitsUsageResults.map(({ objective, usage, error }) => {
                  // "activation" is the internal objective key, and
                  // "audienceTargeting" is an older name for the same thing
                  // (see getObjectiveBadge/getActivationUsageForRow above);
                  const objectiveDisplayName =
                    objective === "activation" || objective === "audienceTargeting"
                      ? "Activation"
                      : capitalize(objective);
                  // Tables that are both enabled and live but never
                  // queried are noise here (always 0/0) - filter them out
                  // rather than show a row nobody can act on, but keep a
                  // count so we can still surface that they exist (see
                  // disabledTableCount below) instead of silently dropping
                  // them. A not-live row is the opposite of noise - it's
                  // real historical usage against a table that's since
                  // disappeared, and hiding it here would recreate the exact
                  // vanishing-usage gap this table exists to close - so it's
                  // always shown (tagged "Historical" by tableStatus)
                  // regardless of its current enabled_flag. When enrichment
                  // has no enabled tables at all, the federation doesn't
                  // really have enrichment - skip the whole section instead
                  // of an empty heading.
                  const visibleTableUsage = usage?.table_usage?.filter(
                    (t) => t.enabled_flag || !t.is_live,
                  );
                  const disabledTableCount =
                    (usage?.table_usage?.length ?? 0) - (visibleTableUsage?.length ?? 0);
                  if (objective === "enrichment" && !error && !visibleTableUsage?.length) {
                    return null;
                  }
                  return (
                  <div key={objective} className={styles.limitsUsageSection}>
                    <div className={styles.limitsUsageSectionTitle}>
                      Query Objective: {objectiveDisplayName}
                    </div>
                    {error ? (
                      <p className={styles.limitsUsageError}>
                        Failed to load limits for {objective}.
                      </p>
                    ) : (
                      <>
                        {!isActivationOnlyFederation &&
                          visibleTableUsage?.length > 0 && (
                          <>
                            <span className={styles.limitsUsageLabelRow}>
                              <strong>Query Limits by Table</strong>
                              <Tooltip
                                text="Each table has its own limit and can run out even if the org total below still has room."
                                placement="top"
                              >
                                <MdInfoOutline
                                  size={13}
                                  className={styles.limitsUsageInfoIcon}
                                />
                              </Tooltip>
                            </span>
                            <table className={styles.limitsUsageTable}>
                              <thead>
                                <tr>
                                  <th>Table</th>
                                  <th>Org</th>
                                  <th>Used</th>
                                  <th>Limit</th>
                                  <th>Left</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleTableUsage.map((t) => {
                                  const status = tableStatus(t);
                                  return (
                                    <tr key={`${t.organization_id}-${t.physical_table_name}`}>
                                      <td>{t.physical_table_name}</td>
                                      <td>{t.organization_name || t.organization_id}</td>
                                      <td>{t.query_count}</td>
                                      <td>{t.query_limit}</td>
                                      <td>{t.query_left}</td>
                                      <td>
                                        <span
                                          className={`${styles.limitsStatusBadge} ${LIMITS_STATUS_CLASS[status.tone]}`}
                                        >
                                          {status.label}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {disabledTableCount > 0 && (
                              <span className={styles.limitsUsageDisabledNote}>
                                {disabledTableCount} more table
                                {disabledTableCount === 1 ? "" : "s"} not enabled - hidden from
                                this list.
                              </span>
                            )}
                          </>
                        )}
                        {usage?.activation_usage && (
                          <>
                            {(() => {
                              const activation = usage.activation_usage;
                              const status = activationStatus(activation);
                              const pct =
                                activation.activation_limit > 0
                                  ? Math.min(
                                      100,
                                      (activation.activation_count /
                                        activation.activation_limit) *
                                        100,
                                    )
                                  : 0;
                              return (
                                <>
                                  <span className={styles.limitsUsageLabelRow}>
                                    <strong>Engagements</strong>
                                    <span
                                      className={`${styles.limitsStatusBadge} ${LIMITS_STATUS_CLASS[status.tone]}`}
                                    >
                                      {status.label}
                                    </span>
                                  </span>
                                  <div className={styles.engagementUsageCard}>
                                    {pct > 0 && (
                                      <>
                                        <span className={styles.engagementProgressLabel}>
                                          Engagement Usage
                                        </span>
                                        <div className={styles.engagementProgressTrack}>
                                          <div
                                            className={`${styles.engagementProgressFill} ${ENGAGEMENT_PROGRESS_CLASS[status.tone]}`}
                                            style={{ width: `${pct}%` }}
                                          />
                                        </div>
                                      </>
                                    )}
                                    <div className={styles.engagementStats}>
                                      <div className={styles.engagementStat}>
                                        <span className={styles.engagementStatValue}>
                                          {activation.activation_count}
                                        </span>
                                        <span className={styles.engagementStatLabel}>
                                          Used
                                        </span>
                                      </div>
                                      <div className={styles.engagementStat}>
                                        <span className={styles.engagementStatValue}>
                                          {activation.activation_limit}
                                        </span>
                                        <span className={styles.engagementStatLabel}>
                                          Limit
                                        </span>
                                      </div>
                                      <div className={styles.engagementStat}>
                                        <span className={styles.engagementStatValue}>
                                          {activation.activation_left}
                                        </span>
                                        <span className={styles.engagementStatLabel}>
                                          Left
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                            {usage.activation_usage.enabled_connectors
                              ?.length > 0 && (
                              <>
                                <span className={styles.limitsUsageLabelRow}>
                                  <strong>Enabled Connectors</strong>
                                </span>
                                <div className={styles.connectorChipList}>
                                  {usage.activation_usage.enabled_connectors.map(
                                    (connector) => {
                                      const { label, icon } =
                                        getConnectorMeta(connector);
                                      return (
                                        <span
                                          key={connector}
                                          className={styles.connectorChip}
                                        >
                                          {icon ? (
                                            <img
                                              src={icon}
                                              alt=""
                                              className={`${styles.connectorChipIcon} ${
                                                connector?.toLowerCase?.() ===
                                                "pubmatic"
                                                  ? styles.connectorChipIconWide
                                                  : ""
                                              }`}
                                            />
                                          ) : (
                                            <span
                                              className={
                                                styles.connectorChipFallbackIcon
                                              }
                                            >
                                              {label?.[0]?.toUpperCase()}
                                            </span>
                                          )}
                                          {label}
                                        </span>
                                      );
                                    },
                                  )}
                                </div>
                              </>
                            )}
                          </>
                        )}
                        {/* {!isActivationOnlyFederation &&
                          usage?.query_limit_usage && (
                          <div className={styles.limitsUsageActivation}>
                            <span className={styles.limitsUsageLabelRow}>
                              <strong>Queries Run (All Users)</strong>
                              <Tooltip
                                text="Total number of queries run across all users in the federation, against all tables."
                                placement="top"
                              >
                                <MdInfoOutline
                                  size={13}
                                  className={styles.limitsUsageInfoIcon}
                                />
                              </Tooltip>
                            </span>
                            <span>
                              {usage.query_limit_usage.query_count}
                            </span>
                          </div>
                        )} */}
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            </InfoPopup>
          )}
        </div>

        {tableData?.length === 0 &&
        !filterApplied &&
        !loading &&
        pageDepth.current === 0 ? (
          <NoData
            {...(failureMessage ? { message: failureMessage } : {})}
          />
        ) : (
          <>
            <div className={styles.container}>
              <div className={styles.tableControls} ref={tableControlsRef}>
                <div className={styles.filterChips}>
                  {Object.entries(filterValue).map(([key, value]) => (
                    <span key={key} className={styles.filterChip}>
                      <strong>{key}:</strong>&nbsp;{value as string}
                      <button
                        className={styles.filterChipRemove}
                        onClick={() => removeFilter(key)}
                        aria-label={`Remove ${key} filter`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
                <div className={styles.filtersContainer}>
                  <Tooltip text="Campaign Analytics" placement="top">
                    <span
                      className={styles.distributionIconBtn}
                      onClick={() => setShowCampaignVolumesModal(true)}
                    >
                      <MdBarChart size={11} />
                      Campaign Analytics
                    </span>
                  </Tooltip>
                  <Tooltip text="Audience Overview" placement="top">
                    <span
                      className={styles.distributionIconBtn}
                      onClick={openOverview}
                    >
                      <svg width="11" height="11" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block", verticalAlign: "middle" }}>
                        <rect x="0" y="7" width="4" height="6" rx="1" fill="currentColor" opacity="0.55"/>
                        <rect x="5" y="3.5" width="4" height="9.5" rx="1" fill="currentColor" opacity="0.75"/>
                        <rect x="10" y="0" width="4" height="13" rx="1" fill="currentColor"/>
                      </svg>
                      Audience Overview
                    </span>
                  </Tooltip>
                  <DownloadButton
                    element_color={getCSSVariable("--Primary-Color-Shade-1")}
                    downloadCSV={() => downloadReport({ fileFormat: "csv" })}
                    downloadJSON={() => downloadReport({ fileFormat: "json" })}
                    downloadPDF={() => downloadReport({ fileFormat: "pdf" })}
                  />
                  <Tooltip text="Query & Engagement Limits" placement="top">
                    <span
                      className={styles.distributionIconBtn}
                      onClick={openLimitsUsagePopup}
                    >
                      <MdSpeed size={11} />
                      Limits Usage
                    </span>
                  </Tooltip>
                  {openSearchQueryIDModal ||
                  datePickerOpen ||
                  showActivationPopup ||
                  openQueryIdPopup ||
                  openFailedInfoPopup ||
                  autoRefreshPaused ||
                  showDeletePopup ||
                  !!breakdownModalData ||
                  isOverviewOpen ? (
                    <div className={styles.refreshTimer}>
                      Auto-refresh paused
                    </div>
                  ) : (
                    <div className={styles.refreshTimer}>
                      Auto-refreshing in: <strong>{refreshIn}s</strong>
                    </div>
                  )}{" "}
                  {autoRefreshPaused ? (
                    <Tooltip text="Resume Auto-refresh" placement="top">
                      <PlayIcon
                        onClick={() => setAutoRefreshPaused(false)}
                        className={styles.primaryColor}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip text="Pause Auto-refresh" placement="top">
                      <PauseIcon
                        onClick={() => setAutoRefreshPaused(true)}
                        className={styles.primaryColor}
                      />
                    </Tooltip>
                  )}
                  <span className={styles.clearFilters} onClick={ClearFilters}>
                    Clear Filters
                  </span>
                  <span
                    className={styles.reload}
                    onClick={() => reloadQueries("RELOAD")}
                  >
                    Refresh
                  </span>
                </div>
              </div>
              <div
                className={styles.tableWrapper}
                style={
                  tableWrapperMaxH !== undefined
                    ? { maxHeight: tableWrapperMaxH }
                    : undefined
                }
              >
                <table className={styles.table} ref={tableElRef}>
                  <thead>
                    <tr>
                      <th></th>
                      <th
                        style={{
                          width:
                            Math.min(
                              parseInt(columnWidths[1], 10),
                              maxFirstColumnWidth,
                            ) + "px",
                          maxWidth: maxFirstColumnWidth + "px",
                        }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader
                          label="Cohort Name | Query ID"
                          icon={<MdDescription />}
                          actionsAlign="right"
                        >
                          <FilterIcon
                            className={styles.icon}
                            onClick={() => setOpenSearchQueryIDModal(true)}
                          />
                        </ColHeader>
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 1)}
                        />

                        {openSearchQueryIDModal && (
                          <SearchModal
                            isOpen={openSearchQueryIDModal}
                            onClose={() => setOpenSearchQueryIDModal(false)}
                            onApply={HandleFilters}
                            headerText="Search Query"
                            errorText="Please enter a Query ID or select a Cohort Name"
                            campaignNames={allCampaignNames}
                            fallbackCampaignNames={volumeCampaignNames}
                          />
                        )}
                      </th>
                      <th
                        style={{ width: columnWidths[2] }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader label="Organization" icon={<MdGroup />}>
                          <DownIcon
                            className={styles.icon}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenParticipantsDropdown(true);
                            }}
                          />
                        </ColHeader>
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 2)}
                        />
                        {openParticipantsDropdown && (
                          <Dropdown
                            items={allOrgs?.map((item) => item.name)}
                            onSelect={getSelectedParticipant}
                            isOpen={openParticipantsDropdown}
                            onClose={() => setOpenParticipantsDropdown(false)}
                          />
                        )}
                      </th>
                      <th
                        style={{ width: columnWidths[3] }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader label="ID Type" icon={<MdFingerprint />}>
                          <DownIcon
                            className={styles.icon}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMatchIDDropdown(true);
                            }}
                          />
                        </ColHeader>
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 3)}
                        />
                        {openMatchIDDropdown && (
                          <Dropdown
                            items={allIdentifiers}
                            onSelect={getSelectedMatchID}
                            isOpen={openMatchIDDropdown}
                            onClose={() => setOpenMatchIDDropdown(false)}
                          />
                        )}
                      </th>
                      <th
                        style={{ width: columnWidths[4] }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader label="Audience Size" icon={<MdNumbers />}>
                          <button
                            className={styles.sortBtn}
                            onClick={() => handleSort("audienceSize")}
                            aria-label={`Sort by audience size ${sortColumn === "audienceSize" ? (sortDirection === "asc" ? "descending" : "ascending") : "ascending"}`}
                          >
                            {sortColumn === "audienceSize" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                          </button>
                        </ColHeader>
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 4)}
                        />
                      </th>
                      <th
                        style={{ width: columnWidths[5] }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader label="Status" icon={<MdCheckCircle />} />
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 5)}
                        />
                      </th>
                      <th
                        style={{ width: columnWidths[6] }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader
                          label="Created At"
                          icon={<MdCalendarToday />}
                          actionsAlign="right"
                        >
                          <button
                            className={styles.sortBtn}
                            onClick={() => handleSort("createdAt")}
                            aria-label={`Sort by created at ${sortColumn === "createdAt" ? (sortDirection === "asc" ? "descending" : "ascending") : "ascending"}`}
                          >
                            {sortColumn === "createdAt" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                          </button>
                          <FilterIcon
                            className={styles.icon}
                            onClick={() => {
                              setFilterApplied(true);
                              setDatePickerOpen(true);
                            }}
                          />
                        </ColHeader>
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 6)}
                        />
                        {datePickerOpen && (
                          <DatePickerModal
                            datePickerOpen={datePickerOpen}
                            onClose={() => {
                              setDatePickerOpen(false);
                            }}
                            onApply={HandleTimestamp}
                            initialStart={filterStartDate || ""}
                            initialEnd={filterEndDate || ""}
                          />
                        )}
                      </th>
                      <th
                        style={{ width: columnWidths[7] }}
                        className={styles.resizableHeader}
                      >
                        <ColHeader
                          label="Actions"
                          icon={<MdPlayArrow />}
                        />
                        <div
                          className={styles.resizeHandle}
                          onMouseDown={(e) => handleMouseDown(e, 7)}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* CHANGED: when loading, render shimmer rows instead of real data.
                    This covers: initial page load, reload button, pagination, and
                    any other call that sets loading=true before awaiting the API. */}
                    {loading ? (
                      Array.from({ length: rowsPerPage }).map((_, i) => (
                        <ShimmerRow key={i} />
                      ))
                    ) : (
                      <>
                        {!tableData?.length &&
                          (refreshIn < 13 || pageDepth.current > 0) && (
                            <tr>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>
                                <NoData />
                              </td>
                              <td></td>
                            </tr>
                          )}
                        {sortedTableData?.map((row, index) => (
                          <Fragment key={row.id}>
                            <tr
                              key={row.id}
                              className={[
                                expandedRows[index] ? styles.expanded : "",
                                expandedRows[index]
                                  ? styles.parentRowActive
                                  : "",
                                highlightedRowId === row.id
                                  ? styles.highlightRow
                                  : "",
                                highlightedSourceIds.has(row.id)
                                  ? styles.sourceHighlightRow
                                  : "",
                              ]
                                .join(" ")
                                .trim()}
                            >
                              <td
                                className={styles.expandableRow}
                                onClick={() =>
                                  !row?.compound_query_input &&
                                  row.status === "SUCCEEDED" &&
                                  toggleRow(index)
                                }
                              >
                                <div className={styles.rowCellContent}>
                                  {row.executed_queries &&
                                    !row?.compound_query_input &&
                                    row.status === "SUCCEEDED" && (
                                      <span
                                        className={`${styles.expandIcon} ${
                                          expandedRows[index]
                                            ? styles.expandedIcon
                                            : ""
                                        }`}
                                      >
                                        <DownIcon />
                                      </span>
                                    )}

                                  {row?.compound_query_input && (
                                    <Tooltip
                                      text={
                                        activeCompoundRowId === row.id
                                          ? "Click to clear highlight"
                                          : "Click to highlight source queries"
                                      }
                                      className={
                                        styles.aggregateQueryCopyIconWrapper
                                      }
                                    >
                                      <span
                                        className={`${styles.compoundIcon} ${activeCompoundRowId === row.id ? styles.compoundIconActive : ""}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (activeCompoundRowId === row.id) {
                                            setActiveCompoundRowId(null);
                                            setHighlightedSourceIds(new Set());
                                          } else {
                                            setActiveCompoundRowId(row.id);
                                            setHighlightedSourceIds(
                                              new Set(
                                                row.compound_query_input.map(
                                                  (c: { query_id: string }) =>
                                                    c.query_id,
                                                ),
                                              ),
                                            );
                                          }
                                        }}
                                      >
                                        𝐂
                                      </span>
                                    </Tooltip>
                                  )}

                                  {enableQueryAggregations &&
                                    !row?.compound_query_input &&
                                    row.status === "SUCCEEDED" &&
                                    !row.is_deleted &&
                                    row.objective !== "enrichment" &&
                                    row.differential_privacy_count_total > 0 &&
                                    Array.from(
                                      new Set(
                                        (row.executed_queries ?? [])
                                          .map((c) => c.contributor_bucket_name)
                                          .filter(Boolean),
                                      ),
                                    ).length >= 1 &&
                                    (row.expiry_duration &&
                                    new Date() >=
                                      new Date(row.expiry_duration) ? (
                                      <Tooltip
                                        text="This query is scheduled for deletion and cannot be selected"
                                        placement="top"
                                      >
                                        <input
                                          type="checkbox"
                                          className={styles.checkbox}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={() => {}}
                                          checked={false}
                                          disabled
                                        />
                                      </Tooltip>
                                    ) : (
                                      <input
                                        type="checkbox"
                                        className={styles.checkbox}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          handleCheckboxChange(
                                            row,
                                            e.target.checked,
                                          )
                                        }
                                        checked={selectedItems.some(
                                          (item) => item.id === row.id,
                                        )}
                                      />
                                    ))}
                                </div>
                              </td>

                              <td
                                className={styles.queryCell}
                                style={{
                                  width:
                                    Math.min(
                                      parseInt(columnWidths[1], 10),
                                      maxFirstColumnWidth,
                                    ) + "px",
                                  maxWidth: maxFirstColumnWidth + "px",
                                }}
                              >
                                <div className={styles.idWithIcons}>
                                  <div
                                    className={styles.idText}
                                    style={{
                                      width:
                                        Math.min(
                                          parseInt(columnWidths[1], 10),
                                          maxFirstColumnWidth,
                                        ) + "px",
                                      maxWidth: maxFirstColumnWidth + "px",
                                    }}
                                  >
                                    <div className={styles.campaignQueryCell}>
                                      <span className={styles.campaignNameText}>
                                        {row.campaign_name}
                                      </span>
                                      <span className={styles.queryIdRow}>
                                        {row.objective && (() => {
                                          const { label, Icon, tone } =
                                            getObjectiveBadge(row.objective);
                                          return (
                                            <Tooltip text={label} placement="top">
                                              <span
                                                className={`${styles.objectiveBadge} ${styles[`objectiveBadge_${tone}`]}`}
                                              >
                                                <Icon size={13} />
                                              </span>
                                            </Tooltip>
                                          );
                                        })()}
                                        <Tooltip
                                          text={`${row.campaign_name} | ${row.id}`}
                                          placement="bottom"
                                          maxWidth={320}
                                        >
                                          <span className={styles.queryIdText}>
                                            {row.id}
                                          </span>
                                        </Tooltip>
                                      </span>
                                      {(() => {
                                        if (row.is_deleted) return null;
                                        const daysLeft = getDaysUntilExpiry(row);
                                        if (daysLeft !== null && daysLeft > 0 && daysLeft <= 3) {
                                          return (
                                            <span className={`${styles.expiryBadge} ${styles.expiryBadgeAmber}`}>
                                              ⚠ Expires soon
                                            </span>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  </div>

                                  <div className={styles.iconContainer}>
                                    <Tooltip
                                      text="Expand"
                                      className={styles.copyIconWrapper}
                                    >
                                      <ExpandIcon
                                        className={styles.expandIcon}
                                        onClick={() =>
                                          setOpenQueryIdPopup(row.id)
                                        }
                                      />
                                    </Tooltip>
                                    {openQueryIdPopup === row.id && (
                                      <InfoPopup
                                        isOpen={true}
                                        onClose={() =>
                                          setOpenQueryIdPopup(null)
                                        }
                                        title="Query Details"
                                      >
                                        <div className={styles.queryDetailBody}>
                                          <div
                                            className={styles.queryDetailRow}
                                          >
                                            <span
                                              className={
                                                styles.queryDetailLabel
                                              }
                                            >
                                              Query ID
                                            </span>
                                            <div
                                              className={
                                                styles.queryDetailValueWrap
                                              }
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailValue
                                                }
                                              >
                                                {row.id}
                                              </span>
                                              <Tooltip
                                                text="Copy"
                                                placement="top"
                                              >
                                                <button
                                                  className={
                                                    styles.queryDetailCopyBtn
                                                  }
                                                  onClick={() => {
                                                    copyToClipboard(row.id);
                                                    setCopiedQueryField(
                                                      "queryId",
                                                    );
                                                    setTimeout(
                                                      () =>
                                                        setCopiedQueryField(
                                                          null,
                                                        ),
                                                      2000,
                                                    );
                                                  }}
                                                >
                                                  <CopyIcon
                                                    className={styles.copyIcon}
                                                  />
                                                </button>
                                              </Tooltip>
                                              {copiedQueryField ===
                                                "queryId" && (
                                                <span
                                                  className={
                                                    styles.queryDetailCopied
                                                  }
                                                >
                                                  Copied!
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          <div
                                            className={styles.queryDetailRow}
                                          >
                                            <span
                                              className={
                                                styles.queryDetailLabel
                                              }
                                            >
                                              Cohort Name
                                            </span>
                                            <div
                                              className={
                                                styles.queryDetailValueWrap
                                              }
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailValue
                                                }
                                              >
                                                {row.campaign_name}
                                              </span>
                                              <Tooltip
                                                text="Copy"
                                                placement="top"
                                              >
                                                <button
                                                  className={
                                                    styles.queryDetailCopyBtn
                                                  }
                                                  onClick={() => {
                                                    copyToClipboard(
                                                      row.campaign_name,
                                                    );
                                                    setCopiedQueryField(
                                                      "campaignName",
                                                    );
                                                    setTimeout(
                                                      () =>
                                                        setCopiedQueryField(
                                                          null,
                                                        ),
                                                      2000,
                                                    );
                                                  }}
                                                >
                                                  <CopyIcon
                                                    className={styles.copyIcon}
                                                  />
                                                </button>
                                              </Tooltip>
                                              {copiedQueryField ===
                                                "campaignName" && (
                                                <span
                                                  className={
                                                    styles.queryDetailCopied
                                                  }
                                                >
                                                  Copied!
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          {row.attributes_selected?.length >
                                            0 && (
                                            <div
                                              className={styles.queryDetailRow}
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailLabel
                                                }
                                              >
                                                Attributes Selected
                                              </span>
                                              <span
                                                className={
                                                  styles.queryDetailValue
                                                }
                                              >
                                                {row.attributes_selected.join(
                                                  ", ",
                                                )}
                                              </span>
                                            </div>
                                          )}

                                          {row.filter_str?.filter(
                                            (s) => s.trim() !== "",
                                          ).length > 0 &&
                                            (() => {
                                              const deduped = Array.from(
                                                new Map(
                                                  row.filter_str
                                                    .filter(
                                                      (s) => s.trim() !== "",
                                                    )
                                                    .map((s) => [
                                                      s.toLowerCase(),
                                                      s,
                                                    ]),
                                                ).values(),
                                              );
                                              return (
                                                <div
                                                  className={
                                                    styles.queryDetailRow
                                                  }
                                                >
                                                  <span
                                                    className={
                                                      styles.queryDetailLabel
                                                    }
                                                  >
                                                    Filter String(s)
                                                  </span>
                                                  <div
                                                    className={
                                                      styles.queryDetailValueWrap
                                                    }
                                                  >
                                                    <div
                                                      className={
                                                        styles.filterStringList
                                                      }
                                                    >
                                                      {deduped.map((s, i) => (
                                                        <Fragment key={i}>
                                                          {i > 0 && (
                                                            <hr
                                                              className={
                                                                styles.filterStringDivider
                                                              }
                                                            />
                                                          )}
                                                          <span
                                                            className={
                                                              styles.queryDetailValue
                                                            }
                                                          >
                                                            {`"${s}"`}
                                                          </span>
                                                        </Fragment>
                                                      ))}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })()}

                                          {row.objective && (
                                            <div
                                              className={styles.queryDetailRow}
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailLabel
                                                }
                                              >
                                                Query Objective
                                              </span>
                                              <span
                                                className={
                                                  styles.queryDetailValue
                                                }
                                              >
                                                {capitalize(row.objective)}
                                              </span>
                                            </div>
                                          )}

                                          {row.status !== "FAILED" && (
                                            <div
                                              className={styles.queryDetailRow}
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailLabel
                                                }
                                              >
                                                Expiry On
                                              </span>
                                              <span
                                                className={
                                                  styles.queryDetailValue
                                                }
                                              >
                                                {isNaN(Date.parse(row.expiry_duration))
                                                  ? row.expiry_duration
                                                  : formatDateTime(row.expiry_duration)}
                                              </span>
                                            </div>
                                          )}

                                          <div
                                            className={styles.queryDetailRow}
                                          >
                                            <span
                                              className={
                                                styles.queryDetailLabel
                                              }
                                            >
                                              Contributor Org(s)
                                            </span>
                                            <div
                                              className={
                                                styles.queryDetailTagList
                                              }
                                            >
                                              {(() => {
                                                const orgs =
                                                  row.executed_queries?.length >
                                                  0
                                                    ? Array.from(
                                                        new Set(
                                                          row.executed_queries.map(
                                                            (c) =>
                                                              c.contributor_organization_name,
                                                          ),
                                                        ),
                                                      )
                                                    : [];
                                                return orgs.length > 0 ? (
                                                  orgs.map((org: string) => (
                                                    <Tooltip
                                                      key={org}
                                                      text={org}
                                                      placement="top"
                                                    >
                                                      <span
                                                        className={
                                                          styles.queryDetailTag
                                                        }
                                                      >
                                                        {org}
                                                      </span>
                                                    </Tooltip>
                                                  ))
                                                ) : (
                                                  <span
                                                    className={
                                                      styles.queryDetailValue
                                                    }
                                                  >
                                                    No contributors
                                                  </span>
                                                );
                                              })()}
                                            </div>
                                          </div>

                                          {(() => {
                                            const buckets = Array.from(
                                              new Set(
                                                (row.executed_queries ?? [])
                                                  .map(
                                                    (c) =>
                                                      c.contributor_bucket_name,
                                                  )
                                                  .filter(Boolean),
                                              ),
                                            );
                                            return buckets.length > 0 ? (
                                              <div
                                                className={
                                                  styles.queryDetailRow
                                                }
                                              >
                                                <span
                                                  className={
                                                    styles.queryDetailLabel
                                                  }
                                                >
                                                  Contributor Dataset(s)
                                                </span>
                                                <div
                                                  className={
                                                    styles.queryDetailTagList
                                                  }
                                                >
                                                  {buckets.map((b: string) => (
                                                    <Tooltip
                                                      key={b}
                                                      text={b}
                                                      placement="top"
                                                    >
                                                      <span
                                                        className={
                                                          styles.queryDetailTag
                                                        }
                                                      >
                                                        {b}
                                                      </span>
                                                    </Tooltip>
                                                  ))}
                                                </div>
                                              </div>
                                            ) : null;
                                          })()}

                                          {row.compound_query_input?.length >
                                            0 && (
                                            <div
                                              className={styles.queryDetailRow}
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailLabel
                                                }
                                              >
                                                Source Query ID(s)
                                              </span>
                                              <div
                                                className={
                                                  styles.queryDetailTagList
                                                }
                                              >
                                                {Array.from(
                                                  new Set(
                                                    row.compound_query_input.map(
                                                      (c: {
                                                        query_id: string;
                                                      }) => c.query_id,
                                                    ),
                                                  ),
                                                ).map((qid: string) => (
                                                  <Tooltip
                                                    key={qid}
                                                    text={qid}
                                                    placement="top"
                                                  >
                                                    <span
                                                      className={`${styles.queryDetailTag} ${styles.queryDetailTagMono}`}
                                                    >
                                                      {qid}
                                                    </span>
                                                  </Tooltip>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {row.compound_query_input?.length >
                                            0 &&
                                            (() => {
                                              const sourceIds = new Set(
                                                row.compound_query_input.map(
                                                  (c: { query_id: string }) =>
                                                    c.query_id,
                                                ),
                                              );
                                              const deduped = Array.from(
                                                new Map(
                                                  tableData
                                                    .filter((r: any) =>
                                                      sourceIds.has(r.id),
                                                    )
                                                    .flatMap(
                                                      (r: any) =>
                                                        r.filter_str ?? [],
                                                    )
                                                    .filter(
                                                      (s: string) =>
                                                        s.trim() !== "",
                                                    )
                                                    .map((s: string) => [
                                                      s.toLowerCase(),
                                                      s,
                                                    ]),
                                                ).values(),
                                              );
                                              if (!deduped.length) return null;
                                              return (
                                                <div
                                                  className={
                                                    styles.queryDetailRow
                                                  }
                                                >
                                                  <span
                                                    className={
                                                      styles.queryDetailLabel
                                                    }
                                                  >
                                                    Filter String(s)
                                                  </span>
                                                  <div
                                                    className={
                                                      styles.queryDetailValueWrap
                                                    }
                                                  >
                                                    <div
                                                      className={
                                                        styles.filterStringList
                                                      }
                                                    >
                                                      {deduped.map((s, i) => (
                                                        <Fragment key={i}>
                                                          {i > 0 && (
                                                            <hr
                                                              className={
                                                                styles.filterStringDivider
                                                              }
                                                            />
                                                          )}
                                                          <span
                                                            className={
                                                              styles.queryDetailValue
                                                            }
                                                          >
                                                            {`"${s}"`}
                                                          </span>
                                                        </Fragment>
                                                      ))}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })()}

                                          {row.description && (
                                            <div
                                              className={styles.queryDetailRow}
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailLabel
                                                }
                                              >
                                                Description
                                              </span>
                                              <span
                                                className={
                                                  styles.queryDetailValue
                                                }
                                              >
                                                {row.description}
                                              </span>
                                            </div>
                                          )}

                                          {row.nl_query && (
                                            <div
                                              className={styles.queryDetailRow}
                                            >
                                              <span
                                                className={
                                                  styles.queryDetailLabel
                                                }
                                              >
                                                AI Query
                                              </span>
                                              <span
                                                className={styles.nlQueryYes}
                                              >
                                                Yes
                                              </span>
                                            </div>
                                          )}

                                          {row.nl_query &&
                                            row.nl_query_params?.input_text && (
                                              <div
                                                className={
                                                  styles.queryDetailRow
                                                }
                                              >
                                                <span
                                                  className={
                                                    styles.queryDetailLabel
                                                  }
                                                >
                                                  User Input
                                                </span>
                                                <span
                                                  className={
                                                    styles.queryDetailValue
                                                  }
                                                >
                                                  {
                                                    row.nl_query_params
                                                      .input_text
                                                  }
                                                </span>
                                              </div>
                                            )}
                                          {!row.is_deleted &&
                                            (cohortPublishOverride[row.id] ??
                                              row.publish_cohort) && (
                                              <PublishedCohortDetails
                                                details={
                                                  publishedDetails[row.id]
                                                }
                                                loading={
                                                  !!publishedLoading[
                                                    cohortCategoryFederation(
                                                      row,
                                                    )
                                                  ]
                                                }
                                                loadFailed={
                                                  !!publishedLoadFailed[
                                                    cohortCategoryFederation(
                                                      row,
                                                    )
                                                  ]
                                                }
                                                onRetry={() =>
                                                  loadPublishedDetails(
                                                    cohortCategoryFederation(
                                                      row,
                                                    ),
                                                  )
                                                }
                                                canEdit={
                                                  row.status === "SUCCEEDED" &&
                                                  cohortPublishLoading !==
                                                    row.id
                                                }
                                                onSaveCpm={(cpm) =>
                                                  savePublishedCpm(row, cpm)
                                                }
                                              />
                                            )}
                                          {row.status === "SUCCEEDED" &&
                                            !row.is_deleted &&
                                            row.differential_privacy_count_total >
                                              0 && (
                                              <div
                                                className={
                                                  styles.queryDetailSubmitRow
                                                }
                                              >
                                                <button
                                                  className={
                                                    styles.queryDetailPublishBtn
                                                  }
                                                  disabled={
                                                    cohortPublishLoading ===
                                                    row.id
                                                  }
                                                  onClick={() => {
                                                    const isPublished =
                                                      cohortPublishOverride[
                                                        row.id
                                                      ] ?? row.publish_cohort;
                                                    if (isPublished) {
                                                      unpublishCohort(row);
                                                    } else {
                                                      setOpenQueryIdPopup(null);
                                                      setPublishCohortRow(row);
                                                      refreshCohortCategories(
                                                        row,
                                                      );
                                                    }
                                                  }}
                                                >
                                                  {cohortPublishLoading ===
                                                  row.id
                                                    ? "..."
                                                    : (cohortPublishOverride[
                                                          row.id
                                                        ] ??
                                                          row.publish_cohort)
                                                      ? "Unpublish"
                                                      : "Publish"}
                                                </button>
                                              </div>
                                            )}
                                        </div>
                                      </InfoPopup>
                                    )}

                                    {storedPermissions?.provenancetrail !==
                                      "no" && (
                                      <Tooltip
                                        text="View corresponding transactions in Blockchain "
                                        className={styles.trailIconWrapper}
                                        style={{
                                          visibility:
                                            row.status === "FAILED"
                                              ? "hidden"
                                              : "visible",
                                        }}
                                      >
                                        <TrailIcon
                                          className={styles.trailIcon}
                                          onClick={() =>
                                            navigateToProvTrailsPage(row.id)
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td style={{ width: columnWidths[2] }}>
                                {" "}
                                <span style={{ width: columnWidths[2] }}>
                                  {row.inquirer_organization_name}{" "}
                                </span>
                              </td>
                              <td style={{ width: columnWidths[3] }}>
                                {" "}
                                <span style={{ width: columnWidths[3] }}>
                                  {identityTypeMap.get(
                                    row.canonical_identity_type,
                                  ) ?? row.canonical_identity_type}
                                </span>
                              </td>
                              <td style={{ width: columnWidths[2], textAlign: row.status === "SUCCEEDED" && row.differential_privacy_count_total === 0 ? "center" : undefined }}>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  {row.status === "SUCCEEDED" &&
                                    !row?.compound_query_input &&
                                    row.differential_privacy_count_total > 0 &&
                                    row.executed_queries?.length > 0 && (
                                      <Tooltip
                                        text="View audience breakdown"
                                        placement="top"
                                      >
                                        <span
                                          className={styles.breakdownCta}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setBreakdownModalData({
                                              queryName:
                                                row.campaign_name || row.id,
                                              queryId: row.id,
                                              campaignName: row.campaign_name,
                                              intersectionCount:
                                                row.differential_privacy_count_total,
                                              executedQueries:
                                                row.executed_queries,
                                              attributesSelected:
                                                row.attributes_selected || [],
                                              nlQuery: row.nl_query,
                                              nlQueryText:
                                                row.nl_query_params
                                                  ?.input_text,
                                            });
                                          }}
                                        >
                                          {/* Audience breakdown / split-bars icon */}
                                          <svg width="13" height="12" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="0" y="7" width="4" height="6" rx="1" fill="currentColor" opacity="0.55"/>
                                            <rect x="5" y="3.5" width="4" height="9.5" rx="1" fill="currentColor" opacity="0.75"/>
                                            <rect x="10" y="0" width="4" height="13" rx="1" fill="currentColor"/>
                                          </svg>
                                        </span>
                                      </Tooltip>
                                    )}
                                  {row.status === "SUCCEEDED"
                                    ? row.differential_privacy_count_total.toLocaleString("en-US")
                                    : <span className={styles.countDash}>—</span>}
                                </span>
                              </td>
                              <td style={{ width: columnWidths[5] }}>
                                <Tooltip text={getStatusLabel(row.status)} placement="top">
                                  <div className={styles.statusCellInner}>
                                    {(row.status === "RUNNING" ||
                                      row.status === "PROCESSING" ||
                                      row.status === "processing") && (
                                      <PercentageLoader
                                        progress={progressMap[row.id] || 0}
                                        size={35}
                                      />
                                    )}
                                    {row.status !== "RUNNING" && (
                                      <img
                                        src={
                                          rowStatusIcons[index] ||
                                          getStatusIcon(row.status)
                                        }
                                        alt="status"
                                        className={styles.statusIcon}
                                      />
                                    )}
                                  </div>
                                </Tooltip>
                              </td>
                              <td style={{ width: columnWidths[6] }}>
                                {(() => {
                                  const parts = formatDateTimeParts(row.created_at);
                                  if (!parts) return <span>—</span>;
                                  return (
                                    <div className={styles.dateTimeCell}>
                                      <span className={styles.dateText}>
                                        {parts.date}
                                      </span>
                                      <span className={styles.timeText}>
                                        {parts.time}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </td>

                              <td
                                className={styles.minWidth200}
                                style={{ maxWidth: columnWidths[7] }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  {!!getMarkButtonText(row) && (
                                    <Tooltip
                                      text={
                                        getMarkButtonText(row) === "Engage"
                                          ? isEngageBlockedByActivationLimit(row)
                                            ? `Activation limit reached (${getActivationUsageForRow(row)?.activation_count}/${getActivationUsageForRow(row)?.activation_limit}). Contact administrator.`
                                            : "Mark for engagement"
                                          : getMarkButtonText(row) === "Expired"
                                            ? `Query expired on ${formatDateTime(row.expiry_duration)} — engagement no longer available`
                                            : getMarkButtonText(row) ===
                                                "Engaged"
                                              ? !isQueryExpired(row) &&
                                                isEngageBlockedByActivationLimit(
                                                  row,
                                                )
                                                ? `Activation limit reached (${getActivationUsageForRow(row)?.activation_count}/${getActivationUsageForRow(row)?.activation_limit}). Contact administrator.`
                                                : `Audience result engaged`
                                              : row.status
                                                  .charAt(0)
                                                  ?.toUpperCase() +
                                                row.status
                                                  .slice(1)
                                                  ?.toLowerCase()
                                      }
                                      className={styles.trailIconWrapper}
                                      style={{ left: 0 }}
                                      placement="top"
                                    >
                                      <span
                                        className={`${styles.action} ${
                                          row.status === "FAILED"
                                            ? styles.failedPillHover
                                            : ""
                                        } ${
                                          styles[
                                            rowIcons[index]?.toLowerCase() ||
                                              (getMarkButtonText(row) ===
                                                "Engaged" && isQueryExpired(row)
                                                ? "engaged_deleted"
                                                : (getMarkButtonText(row) ===
                                                      "Engage" ||
                                                    getMarkButtonText(row) ===
                                                      "Engaged") &&
                                                    !isQueryExpired(row) &&
                                                    isEngageBlockedByActivationLimit(
                                                      row,
                                                    )
                                                  ? "limitReached"
                                                  : sanitizeClassName(
                                                      getMarkButtonText(
                                                        row,
                                                      )?.toLowerCase(),
                                                    ))
                                          ]
                                        } ${
                                          (isQueryExpired(row) &&
                                            getMarkButtonText(
                                              row,
                                            )?.toLowerCase() === "engaged") ||
                                          (getMarkButtonText(row)?.toLowerCase() ===
                                            "engage" &&
                                            isEngageBlockedByActivationLimit(row)) ||
                                          (getMarkButtonText(row)?.toLowerCase() ===
                                            "engaged" &&
                                            !isQueryExpired(row) &&
                                            isEngageBlockedByActivationLimit(row) &&
                                            !readAccess)
                                            ? styles.cursorNotAllowed
                                            : [
                                                  "engage",
                                                  "cancel",
                                                  "engaged",
                                                ].includes(
                                                  rowIcons[
                                                    index
                                                  ]?.toLowerCase(),
                                                ) ||
                                                ["engage", "cancel"].includes(
                                                  getMarkButtonText(
                                                    row,
                                                  )?.toLowerCase(),
                                                ) ||
                                                (getMarkButtonText(
                                                  row,
                                                )?.toLowerCase() ===
                                                  "engaged" &&
                                                  !readAccess)
                                              ? styles.cursorPointer
                                              : ""
                                        }`}
                                        onMouseEnter={() => {
                                          if (
                                            getMarkButtonText(row) ===
                                              "Engaged" &&
                                            !readAccess &&
                                            !isQueryExpired(row) &&
                                            !isEngageBlockedByActivationLimit(
                                              row,
                                            )
                                          ) {
                                            setHoveredIndex(index);
                                          }
                                        }}
                                        onMouseLeave={() => {
                                          if (hoveredIndex === index) {
                                            setHoveredIndex(null);
                                          }
                                        }}
                                        onClick={() => {
                                          if (
                                            row.status === "FAILED" &&
                                            row.ui_error_text
                                          ) {
                                            setOpenFailedInfoPopup(row.id);
                                            return;
                                          }
                                          if (
                                            getMarkButtonText(row)?.toLowerCase() ===
                                              "engage" &&
                                            isEngageBlockedByActivationLimit(row)
                                          ) {
                                            return;
                                          }
                                          if (
                                            getMarkButtonText(row)?.toLowerCase() ===
                                              "engaged" &&
                                            !isQueryExpired(row) &&
                                            isEngageBlockedByActivationLimit(row)
                                          ) {
                                            return;
                                          }
                                          if (
                                            (!rowIcons[index]?.toLowerCase() &&
                                              [
                                                "engage",
                                                "cancel",
                                                "engaged",
                                              ].includes(
                                                rowIcons[index]?.toLowerCase(),
                                              )) ||
                                            ["engage", "cancel"].includes(
                                              getMarkButtonText(
                                                row,
                                              )?.toLowerCase(),
                                            ) ||
                                            (getMarkButtonText(
                                              row,
                                            )?.toLowerCase() === "engaged" &&
                                              !readAccess &&
                                              !isQueryExpired(row))
                                          ) {
                                            handleActive(row, index);
                                          }
                                        }}
                                      >

                                        {getMarkButtonText(row) ===
                                        "loading" ? (
                                          <Tooltip
                                            text="Cancel this query"
                                            placement="top"
                                          >
                                            <img
                                              src={DeletedIcon}
                                              alt="cancel"
                                              className={`${styles.cancelIcon} ${styles.cursorPointer}`}
                                              onClick={() =>
                                                handleDeleteQuery(row, index)
                                              }
                                            />
                                          </Tooltip>
                                        ) : getMarkButtonText(row) === "" ? (
                                          ""
                                        ) : (
                                          rowIcons[index] ||
                                          (getMarkButtonText(row) ===
                                            "Engaged" &&
                                          hoveredIndex === index &&
                                          !readAccess &&
                                          !isQueryExpired(row) ? (
                                            <>Engage Again!</>
                                          ) : getMarkButtonText(row) ===
                                              "Engaged" &&
                                            isQueryExpired(row) ? (
                                            getEngagedLabel(row, " & Expired")
                                          ) : getMarkButtonText(row) ===
                                            "Engaged" ? (
                                            getEngagedLabel(row)
                                          ) : getMarkButtonText(row) ===
                                            "Engaged & Deleted" ? (
                                            getEngagedLabel(row, " & Deleted")
                                          ) : getMarkButtonText(row) ===
                                            "Engage" ? (
                                            getEngageLabel(row)
                                          ) : (
                                            getMarkButtonText(row)
                                          ))
                                        )}
                                        {row.status === "FAILED" &&
                                          row.ui_error_text && (
                                            <InforIcon
                                              className={styles.infoIconSvg}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenFailedInfoPopup(row.id);
                                              }}
                                            />
                                          )}
                                      </span>
                                    </Tooltip>
                                  )}
                                  {openFailedInfoPopup === row.id && (
                                    <InfoPopup
                                      isOpen={true}
                                      onClose={() =>
                                        setOpenFailedInfoPopup(null)
                                      }
                                      title="Query Failed"
                                      displayText={row.ui_error_text}
                                      closeButtonLabel="Got it"
                                      freeText
                                    />
                                  )}

                                  {row.status === "SUCCEEDED" &&
                                    row.differential_privacy_count_total ===
                                      0 &&
                                    !row.is_deleted &&
                                    !row.marked_for_activation && (
                                      <Tooltip
                                        text="No action possible because no matching records were found."
                                        placement="top"
                                      >
                                        <span className={styles.noResultsBadge}>
                                          <span
                                            className={styles.noResultsGlyph}
                                          >
                                            ∅
                                          </span>
                                          No results
                                        </span>
                                      </Tooltip>
                                    )}

                                  {/* {row.status === "FAILED" && !readAccess && (
                                    <Tooltip
                                      text="Retry query with same filters"
                                      placement="top"
                                    >
                                      <button
                                        className={styles.retryBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRetryQuery(row);
                                        }}
                                      >
                                        <ReloadIcon
                                          className={styles.retryBtnIcon}
                                        />
                                      </button>
                                    </Tooltip>
                                  )} */}

                                  {["engage", "engaged"].includes(
                                    getMarkButtonText(row)?.toLowerCase(),
                                  ) &&
                                    !readAccess &&
                                    (getMarkButtonText(row) === "" ? (
                                      <ButtonLoader />
                                    ) : (
                                      <Tooltip
                                        text="Delete this query"
                                        placement="top"
                                      >
                                        <img
                                          src={StopIcon}
                                          alt="Stop"
                                          className={styles.stopIcon}
                                          onClick={() =>
                                            setShowDeletePopup({
                                              row,
                                              index,
                                              actionType: "delete",
                                            })
                                          }
                                        />
                                      </Tooltip>
                                    ))}
                                  {["engaged", "engaged & deleted"].includes(
                                    getMarkButtonText(row)?.toLowerCase(),
                                  ) &&
                                    (getMarkButtonText(row) === "" ? (
                                      <ButtonLoader />
                                    ) : storedPermissions.activationhistory !==
                                      "no" ? (
                                      <Tooltip
                                        text="View audience history."
                                        placement="top"
                                      >
                                        <HistoryIcon
                                          className={styles.reloadIcon}
                                          onClick={() =>
                                            navigateToCampaignHistoryPage(
                                              row.id,
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    ) : null)}
                                </span>
                              </td>
                            </tr>
                            {expandedRows[index] && (
                              <>
                                {row.executed_queries?.map((result, idx) => {
                                  return (
                                    <>
                                      <tr
                                        key={result.subquery_id || idx}
                                        className={styles.childRowActive}
                                      >
                                        <td
                                          className={`${styles.expandableRow} ${styles.verticalLine}`}
                                        ></td>
                                        <td>
                                          <Tooltip
                                            text={result.subquery_id}
                                            placement="top"
                                          >
                                            <span>
                                              {result.subquery_id?.slice(0, 25)}
                                            </span>
                                          </Tooltip>
                                        </td>

                                        <td>
                                          {result.contributor_organization_name}
                                        </td>
                                        <td>
                                          {identityTypeMap.get(
                                            row.canonical_identity_type,
                                          ) ?? row.canonical_identity_type}
                                        </td>
                                        <td>
                                          {(result.differential_privacy_query
                                            ?.type === "DP"
                                            ? result.differential_privacy_query
                                                .result_count
                                            : result.audience_result_count
                                          ).toLocaleString("en-US")}
                                        </td>
                                        <td></td>
                                        <td className={styles.noWrap}>
                                          {(() => {
                                            const parts = formatDateTimeParts(row.created_at);
                                            if (!parts) return <span>—</span>;
                                            return (
                                              <div
                                                className={styles.dateTimeCell}
                                              >
                                                <span
                                                  className={styles.dateText}
                                                >
                                                  {parts.date}
                                                </span>
                                                <span
                                                  className={styles.timeText}
                                                >
                                                  {parts.time}
                                                </span>
                                              </div>
                                            );
                                          })()}
                                        </td>
                                        <td></td>
                                      </tr>
                                    </>
                                  );
                                })}
                              </>
                            )}
                          </Fragment>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className={styles.tableFooter} ref={tableFooterRef}>
              <button
                className={
                  pageDepth.current === 0
                    ? styles.navigationDisable
                    : styles.prevPage
                }
                onClick={handlePreviousPageClick}
              >
                Previous Page
              </button>

              <span className={styles.pageIndicator}>Page {currentPage}</span>

              <button
                className={
                  tableData?.length < rowsPerPage || isLastPage
                    ? styles.navigationDisable
                    : styles.nextPage
                }
                disabled={tableData?.length < rowsPerPage || isLastPage}
                onClick={handleNextPageClick}
              >
                Next Page
              </button>
            </div>
          </>
        )}
      </div>
    </LayoutContainer>
  );
};

export default PastQueries;
